/**
 * ── THE LIGHT ENTITY MODEL — WHAT A MOBILITY MEANS *ON THIS STACK* (E2) ─────────────────────────
 *
 * PURE (`tests/purity.test.ts` → `PURE_FILES`): no three, no DOM, no clock. It is the metadata
 * half of the light system — the vocabulary a world file may use, and the table that says which
 * (type × mobility) pairs this pipeline can actually honour. `worldLights.ts` is where `three`
 * enters and it reads this table; nothing pure may reach the other way.
 *
 * It is pure for the same reason `bakeQuality.ts` and `debugView/registry.ts` are: **a wrong row
 * here does not fail.** It silently deletes an artist's light, or silently doubles it, and every
 * self-report stays green. Pure ⇒ the whole table is asserted in `npm run check` with no GPU.
 *
 * ── ⚠️ THE ONE THING THAT MAKES UE'S MEANINGS NOT TRANSFER: WE HAVE NO LIGHTMAPS ────────────────
 *
 * In Unreal a STATIC light's DIRECT lighting survives the light's removal because it was baked
 * into a lightmap on the surface. Here the only baked product is **irradiance at probe positions
 * in the air** — the sparse SH volume (`render/gi/`). A surface's direct lighting is never baked
 * anywhere. Therefore:
 *
 *     Marking a light `static` in this engine DELETES ITS DIRECT LIGHTING. What survives is its
 *     one-bounce INDIRECT, carried by the probe field, which is much dimmer and much softer.
 *
 * That sentence is `MOBILITY_NOTE.static`, verbatim, and it is what the editor shows on the
 * option. It is not a caveat to bury; it is the mobility model.
 *
 * The counterpart is the good half, and it is why `stationary` is a real arm rather than a synonym
 * for `movable`: **a probe capture never photographs a punctual light itself.** A point or spot
 * light has no emissive geometry, so `bakeCapturePass`'s cube render sees only the SURFACES it lit
 * — the radiance integrated into the SH is the light's BOUNCE, not its beam. So a light left in
 * the scene is direct-at-runtime + bounce-in-the-atlas, which is exactly UE's stationary
 * semantics, arrived at by construction rather than by a lightmap. (The sun gets there a different
 * way and it is already measured: `src/render/sky.ts` trap 4 hides the solar disc for every
 * capture, so its direct beam is excluded from the atlas too.)
 *
 * ⚠️ THAT ARGUMENT IS NOT EVIDENCE, AND IT WAS NOT TRUSTED. `src/render/indirect.ts` states the
 * OPPOSITE rule for bake-only lights — *"a light that is baked MUST NOT also be present at
 * runtime"* — and it is right about the light it names. The claim above is narrower, so it was
 * MEASURED: `tests/e2e/lights.e2e.ts`, four `?gi=bake` boots of one world differing only in the
 * light, one pinned camera on a wall lit by the bounce off the floor in front of it
 * (2026-09-02, sandbox-01 + one bounce wall, 1,445 probes, `medium`):
 *
 *     arm            wall luminance      what it is
 *     no light            74.72          the room without the lamp
 *     STATIC              78.77          the lamp is IN THE ATLAS ONLY  →  bounce +4.04
 *     movable            149.43          the lamp is LIVE ONLY         →  direct +74.71
 *     stationary         151.13          live AND in the atlas         →  bounce +1.69 again
 *
 * **bounce / direct = 0.054.** The capture photographed the surfaces the lamp lit, not the lamp:
 * a beam would have arrived at the direct term's magnitude, and this is a twentieth of it. So
 * `stationary` (direct at runtime PLUS this in the atlas) is not a double count, and `static`
 * genuinely leaves a room lit by a light that is not there. The second measurement is smaller
 * than the first because it lands high on ACES's shoulder, not because it is a different quantity.
 *
 * ⚠️ The first version of that run measured `stationary − movable = −0.02` and it was the code
 * that was wrong, not the model: `bakeCapturePass` photographs the SCENE GRAPH, so a `movable`
 * light left parented is in the bake, and the arm that was supposed to be the no-bounce control
 * was not one. `WorldLightRig.excludedFromBake()` is the fix. Mobility takes lights OUT of the
 * bake as well as putting them in.
 *
 * ── WHY A LIGHT IS NOT A REGISTRY ENTITY (ADR-0001), STATED ONCE ────────────────────────────────
 *
 * Registry classes are LAZY and CODE-BEARING; a light is neither. Three consequences decide it:
 * (1) a lazily-spawned light arrives AFTER material compilation, and `LightsNode.customCacheKey()`
 * hashes every light's id into every material's pipeline key — so the world's lighting landing
 * late would rebuild every shader in the scene; (2) `probeFile.liveLighting()` builds the
 * provenance fingerprint by READING the light objects, so a light present on some boots and absent
 * on others would refuse atlases at random; (3) `world.lighting` is already the established home
 * for exactly this decision (`schema.ts`'s G4 block), and a second home for the fifth light while
 * the first four live in `lighting` is the two-answers-to-one-question shape this codebase names
 * as its recurring failure. So: `world.lighting.lights[]`, data, read once.
 */

/** The vocabulary a world file may use. `z.enum(LIGHT_TYPES)` in `src/world/schema.ts`. */
export const LIGHT_TYPES = ['directional', 'point', 'spot', 'skylight'] as const;
export type LightType = (typeof LIGHT_TYPES)[number];

/** UE's three mobilities, redefined for a pipeline with probe volumes and no lightmaps. */
export const LIGHT_MOBILITIES = ['static', 'stationary', 'movable'] as const;
export type LightMobility = (typeof LIGHT_MOBILITIES)[number];

/**
 * WHERE A LIGHT EXISTS, which is the only thing mobility actually controls:
 *
 *   'runtime' — constructed and added to the scene graph. Costs its per-frame price.
 *   'bake'    — constructed, marked `BAKE_ONLY` (`src/render/indirect.ts`), and parented ONLY for
 *               the duration of a bake. At runtime it is not in the graph at all, so it is not in
 *               `LightsNode`'s cache key and not in the shader's loop: free.
 *   'none'    — refused. Nothing is constructed and the reason is DATA the editor renders.
 */
export type LightPresence = 'runtime' | 'bake' | 'none';

export interface LightSupport {
  supported: boolean;
  presence: LightPresence;
  /** Is this light in the scene while a probe bake runs? (`runtime` stationary and `bake` are.) */
  inBake: boolean;
  /** One sentence an artist reads. For a refusal it names WHY and WHERE the supported thing is. */
  note: string;
}

/**
 * What each mobility means HERE. Shown on the editor's mobility picker — the `static` line is the
 * one that stops an artist expecting Unreal and watching the room go dark.
 */
export const MOBILITY_NOTE: Record<LightMobility, string> = {
  static:
    'Baked into the probe volume, then ABSENT at runtime — zero per-frame cost, zero light slot, ' +
    'zero pipeline entry. WE HAVE NO LIGHTMAPS: only its one-bounce INDIRECT survives, carried by ' +
    'the probe field. Its DIRECT light is gone. Re-bake after any change.',
  stationary:
    'In the scene AND in the bake: full dynamic direct light and shadows, plus its bounce in the ' +
    'probe volume (a probe capture photographs the surfaces a punctual light lit, never the light ' +
    'itself). Costs exactly what movable costs. Re-bake after any change.',
  movable:
    'In the scene, NOT in the bake. Move it, recolour it, animate it — the atlas is unaffected ' +
    'because its light was never in it. The only arm needing no re-bake, and the only one that ' +
    'contributes no bounce.',
};

/**
 * ⚠️ MOBILITY DOES NOT CONTROL SHADOW CACHING — AND L1 LEFT THIS NOTE'S *REASON* WRONG.
 *
 * The CONCLUSION is right and unchanged: a `stationary` light costs a full shadow pass exactly
 * like a `movable` one, because nothing about mobility makes three re-use a depth pass.
 *
 * The reason given until L2 was that "there is no static/dynamic split to hang stationary on".
 * That is FALSE in r185, and the falseness matters, because it is the sentence that would stop
 * somebody building the split. `ShadowNode.updateShadow()` reads:
 *
 *     const _shadowCameraLayer = shadow.camera.layers.mask;
 *     if ( ( shadow.camera.layers.mask & 0xFFFFFFFE ) === 0 ) {
 *         shadow.camera.layers.mask = camera.layers.mask;
 *     }
 *
 * — the shadow camera inherits the MAIN camera's layer mask **only while it is still on the
 * default layer alone**. Put it on any other layer and the mask survives, and
 * `Renderer._projectObject` filters the pass by `object.layers.test( camera.layers )`. So a shadow
 * map CAN be rendered from static geometry only. And `ShadowNode.updateBefore()` is
 * `let needsUpdate = shadow.needsUpdate || shadow.autoUpdate;` — with `autoUpdate = false` the
 * depth pass does not run, the render target and its depth texture persist, and the shader keeps
 * sampling them.
 *
 * ⇒ The split exists, it is `SHADOW_UPDATES` below, and it is its OWN authored field rather than a
 * consequence of mobility. UE couples the two; we cannot honour the coupling (three gives ONE
 * shadow map per light, so there is nowhere to put UE's second, per-object dynamic map), and
 * inventing a coupling we cannot honour is the silent downgrade this whole table exists to
 * prevent. So mobility keeps saying what it always said, and this string now says why.
 */
export const SHADOW_CACHING_NOTE =
  'Mobility does not make a shadow cheaper: a stationary light costs a full shadow pass exactly ' +
  'like a movable one. The lever is this light’s own Shadow update field, which is separate on ' +
  'purpose — three gives one shadow map per light, so there is nowhere to put UE’s second one.';

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ── ⭐ L2 · THE SHADOW UPDATE POLICY — THE ONE LEVER HERE THAT REMOVES ms RATHER THAN COUNTS IT ─
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Measured (R1.5-B, tier 0, light count held at 8 so shadows were the only variable): a live
 * shadow caster is **1.04 ms on sandbox-01 and 2.96 ms on Sponza**, +33 draws and +111,803
 * triangles each. A 16.6 ms frame is fully consumed by ~5-6 shadow casters and nothing else, and
 * **dynamic resolution cannot touch any of it** — a shadow pass is a SCENE TRAVERSAL, so it scales
 * with content and not with pixels.
 *
 * Everything else in the optimisation list either counts that cost (`shadowTraversals`,
 * `shadowMapBytes`) or moves it. This is the one field that deletes it, and it is free to add
 * because `shadow.autoUpdate`, `shadow.needsUpdate` and `shadow.camera.layers` are **not in any
 * pipeline cache key** — unlike `light.castShadow` and `light.visible`, which are. See
 * `LIGHT_KEY_FREE_FIELDS` below: that difference is the whole of L2's cache-key discipline.
 *
 * ── ⚠️ WHY IT IS ONE ENUM AND NOT TWO FIELDS ────────────────────────────────────────────────────
 *
 * The obvious shape is two: WHAT is in the map (`live | staticOnly`) and WHEN it re-renders
 * (`everyFrame | everyNFrames | onDemand`). That is six combinations of which two are incoherent
 * (`staticOnly` content refreshed every frame is paying full price for a map that cannot change),
 * one is a synonym, and an artist is left to discover which pairs are real. Six controls for three
 * behaviours is the two-answers-to-one-question shape this codebase keeps losing to. One enum,
 * three arms, each a distinct thing the frame actually does:
 *
 *   'everyFrame'    the shipped behaviour, and still the default. Full traversal, every frame.
 *   'everyNFrames'  the same map, re-rendered every Nth frame. A 2.96 ms caster amortises to
 *                   0.74 ms at N = 4. The artefact is a shadow LAGGING a moving caster, which at
 *                   the range you would use this is sub-pixel by construction.
 *   'staticOnly'    rendered ONCE, from static geometry only, then frozen. Steady-state cost 0.
 *                   ⚠️ NOTHING THAT MOVES CASTS A SHADOW FROM THIS LIGHT — the player included.
 *                   A refusal-class fact, not a downgrade, so it is a NOTE on the row.
 *
 * `'staticOnly'` needs BOTH halves or it is worse than useless: freezing the map without
 * restricting the camera's layers burns whatever was moving at freeze time into the map forever —
 * a permanent ghost of the player standing at spawn. `FWG_STATIC_SHADOW_LAYER` is the other half.
 *
 * ── ⭐ B4 · THE FOURTH ARM, `'budgeted'` — THE STATIONARY SPLIT AS A POLICY OVER THE OTHER THREE ─
 *
 * UE's stationary model gives a light TWO maps: static casters baked once, movables in a small
 * live map. three gives ONE map per light, so the split cannot be per-object — but it CAN be
 * per-light over time: the nearest N shadowed lights are LIVE (the `everyFrame` arm — static and
 * movable casters, every frame) and every other one is FROZEN (the `staticOnly` arm — rendered
 * once from static geometry, camera on `FWG_STATIC_SHADOW_LAYER`, zero cost until it is nearest
 * again). N is what the tier can afford after the sun's cascades — `localShadowBudget` — and it
 * is 1 at tier 2, 0 below, so a shadowed POINT light (six traversals) never fits and is always
 * frozen, and the row says so rather than letting the artist discover it.
 *
 * It is a fourth ARM rather than a second field because the alternative is a `budgeted: boolean`
 * beside `shadowUpdate` — four combinations, two of them incoherent (`staticOnly + budgeted` is
 * frozen twice) — and "one enum, each arm a distinct thing the frame does" is the rule stated
 * above. Every transition it makes is `autoUpdate` / `needsUpdate` / `camera.layers`, all in
 * `LIGHT_KEY_FREE_FIELDS`, so a light gaining or losing its slot rebuilds NO pipeline.
 * `effectiveShadowUpdate` is the ONE resolver of what a budgeted light does — the cost model and
 * the runtime both read it, which is what makes the row's promise and the frame's behaviour the
 * same function rather than two that agree today.
 */
export const SHADOW_UPDATES = ['everyFrame', 'everyNFrames', 'staticOnly', 'budgeted'] as const;
export type ShadowUpdate = (typeof SHADOW_UPDATES)[number];

/**
 * The three arms a `LightShadow` can actually be put into. `'budgeted'` is a POLICY that resolves
 * to one of these per light per selection — `effectiveShadowUpdate` — and `applyShadowUpdatePolicy`
 * takes this type so it cannot be handed the policy by mistake.
 */
export type AppliedShadowUpdate = Exclude<ShadowUpdate, 'budgeted'>;

/**
 * ⭐ B6 — THE SAME THREE ARMS AS A VALUE, SO `WorldSunSchema` CAN SPELL A ZOD ENUM OF THEM.
 *
 * The sun authors `AppliedShadowUpdate`, not `ShadowUpdate`, and the missing arm is a REFUSAL made
 * unrepresentable rather than a refusal reported: `'budgeted'` means "live while this light is one
 * of the nearest the tier can afford AFTER THE SUN" (`localShadowBudget`), so a budgeted sun would
 * be competing for the traversals left over by itself. An authored field that does nothing is the
 * failure this repo has thirteen logged instances of; a field zod will not accept is none of them.
 *
 * A second literal list of the same strings is exactly the shape this file calls its recurring
 * failure, so the fence is a TEST, not discipline: `tests/renderLightRig.test.ts` asserts this list
 * is EXACTLY `SHADOW_UPDATES` minus `'budgeted'`. (`satisfies` catches a typo or an extra arm at
 * compile time; only the test catches a NEW arm nobody added here.)
 */
export const APPLIED_SHADOW_UPDATES = [
  'everyFrame',
  'everyNFrames',
  'staticOnly',
] as const satisfies readonly AppliedShadowUpdate[];

/** What each arm means, at the control. `everyFrame` is what every light did before L2. */
export const SHADOW_UPDATE_NOTE: Record<ShadowUpdate, string> = {
  everyFrame:
    'A full depth pass over the scene every frame - ~2.96 ms measured on Sponza, and SIX of them ' +
    'for a point light. Correct for everything, and the only arm with no artefact.',
  everyNFrames:
    'The same shadow map, re-rendered every Nth frame. At N=4 a 2.96 ms caster averages 0.74 ms. ' +
    'A moving caster’s shadow LAGS it by up to N frames, so this is for lights far enough away ' +
    'that the lag is sub-pixel. The refresh frame still costs the whole pass.',
  staticOnly:
    'Rendered ONCE from static geometry only, then frozen: zero cost per frame, forever. ' +
    '⚠ NOTHING THAT MOVES CASTS A SHADOW FROM THIS LIGHT - the player included. It is re-rendered ' +
    'when the light or the world is edited, never on its own.',
  budgeted:
    'LIVE (a full depth pass every frame, movers included) while this light is one of the nearest ' +
    'the tier can afford after the sun; FROZEN (static geometry only, zero cost) otherwise. Tier 2 ' +
    'affords ONE live traversal beside the sun’s cascades, so a shadowed POINT light (six) is ' +
    'always frozen. Needs a stationary light: a moving light cannot freeze its map.',
};

/** Which of the live slots a `budgeted` light holds right now. `'n/a'` for every other arm. */
export type ShadowSlot = 'live' | 'frozen' | 'n/a';

/**
 * ⭐ THE ONE RESOLVER of what a light's shadow ACTUALLY does this frame. The three honoured arms
 * pass through; `'budgeted'` becomes `everyFrame` while the light holds a slot and `staticOnly`
 * while it does not. Two exceptions, both stated on the row rather than silently applied:
 *
 *  - a `movable` light cannot freeze — its frozen map would be a picture from wherever it stood
 *    when it froze — so `budgeted` on a movable light reads as `everyFrame`
 *    (`SHADOW_BUDGETED_MOVABLE_NOTE`), the same shape as `STATIC_SHADOW_UPDATE_INERT_NOTE`;
 *  - a `static` light has no runtime pass at all; the existing inert note covers it and the
 *    caller never reaches this.
 *
 * `slot === 'n/a'` on a budgeted light means NO SELECTION HAS BEEN MADE (a pure caller with no
 * camera). It resolves LIVE — the peak — because an unmigrated caller must never quote a cheaper
 * number than the frame could draw (`shadowTraversals`' compatibility rule).
 */
export function effectiveShadowUpdate(
  update: ShadowUpdate,
  mobility: LightMobility,
  slot: ShadowSlot,
): AppliedShadowUpdate {
  if (update !== 'budgeted') return update;
  if (mobility === 'movable') return 'everyFrame';
  return slot === 'frozen' ? 'staticOnly' : 'everyFrame';
}

/**
 * ── ⭐ THE LAYER THAT MAKES A FROZEN SHADOW MAP HONEST ──────────────────────────────────────────
 *
 * A NON-DEFAULT layer, because `ShadowNode.updateShadow`'s inherit branch is
 * `( shadow.camera.layers.mask & 0xFFFFFFFE ) === 0` — i.e. "is this camera on layer 0 and nothing
 * else". Layer 0 is what every object in this codebase is on (verified: nothing under `src/`
 * touches `Object3D.layers` at all), so ANY channel 1..31 keeps our mask. 3 is chosen rather than 1
 * to leave 1 and 2 for the two things people reach for first — a UI-only camera and an editor-only
 * camera — so adding either later does not have to move this one.
 *
 * ⚠️ THE OPT-IN DIRECTION IS FORCED, NOT PREFERRED. `Layers.test` is a bitwise AND over an OR-built
 * mask, so there is no "everything EXCEPT": a dynamic object marked with an extra channel would
 * still match any mask containing layer 0. The only workable direction is POSITIVE — static
 * geometry opts IN to this channel and a frozen shadow camera renders that channel alone.
 * `worldLights.markDynamicShadowCaster()` is the opt-OUT, and it skips a whole SUBTREE, because
 * the character mesh arrives under the player group long after the player group does.
 */
export const FWG_STATIC_SHADOW_LAYER = 3;

/**
 * ── ⭐ R5 · THE COMPLEMENT CHANNEL — MOVERS ONLY ────────────────────────────────────────────────
 *
 * `FWG_STATIC_SHADOW_LAYER` above is the channel a FROZEN camera renders. The two-map split needs
 * the other half: a channel the LIVE cascades render, holding only what moves. It has to be a
 * second positive channel for exactly the reason stated above — `Layers.test` is a bitwise AND, so
 * "everything except the static things" is not expressible and both sides must opt in.
 *
 * ⭐ THE TWO WALKS SHARE ONE PREDICATE, AND THAT IS WHAT MAKES THE PARTITION HONEST.
 * `worldLights.enableStaticShadowLayer` opts an object in when it is NOT under a
 * `DYNAMIC_CASTER_USERDATA` subtree; `worldLights.enableDynamicShadowLayer` opts it in when it IS.
 * One mark decides both, so an object cannot land in both channels (drawn twice, and double-
 * darkened by the `min` composition) or in neither (no shadow at all, from a light that is on).
 * Two independently-authored predicates is precisely how this repo produced a static channel that
 * silently excluded the entire level.
 *
 * 4, following 3's argument: 1 and 2 stay reserved for a UI-only and an editor-only camera.
 */
export const FWG_DYNAMIC_SHADOW_LAYER = 4;

/**
 * ── ⭐ R5 · THE TWO-MAP SPLIT, AS A POLICY RATHER THAN AS A MECHANISM ───────────────────────────
 *
 * MEASURED, `mars-hangar-01`, tier 2, the shipped 3-cascade rig: the sun's three cascades cost
 * **30.3 ms of an 86.9 ms frame**, spread 5.6 / 8.6 / 16.1 over 213 / 436 / 1,087 draws — and the
 * level is **5,563 static meshes and one moving player**. Every cascade re-renders the entire
 * static world every frame to produce a depth buffer that, for the static half, is identical to
 * last frame's. The split is the structural answer: render the static world's shadow ONCE and
 * reuse it, and let the live cascades carry only what moves.
 *
 * ── WHAT IT IS, IN ONE LINE ─────────────────────────────────────────────────────────────────────
 *
 *     sun.shadow.shadowNode = min( staticShadowNode, <today's cascade term> )
 *
 * `min` because occlusion composes by "either occluder darkens", and because the terms are over
 * DISJOINT object sets — the partition above guarantees no double-count of one caster.
 *
 * ── ⚠️ WHY THE STATIC MAP FOLLOWS THE CAMERA IN STEPS, WHICH IS THE ONE HARD QUESTION ───────────
 *
 * "Frozen" and "camera-fitted" are in genuine tension: a cascade box is re-fitted to the camera's
 * frustum slice every frame, so freezing that pass gives a map of where the camera USED to be —
 * the defect `sunShadowPolicy`'s `staticOnly` arm resolves by falling back to a WORLD-ANCHORED
 * box, and the defect `csmRig.detachForBake` exists to keep out of the GI bake.
 *
 * ⛔ A WORLD-ANCHORED BOX DOES NOT WORK ON THIS LEVEL, AND THE NUMBER IS WHY. `mars-hangar-01`'s
 * 5,564 placements span **3,921 × 1,574 × 3,416 m**, because ~99 distant backdrop props sit out to
 * 2.2 km — but the p50 radius is **121 m** and the p95 is **240 m**. One box over the whole level
 * at 2048² would be ~1.9 m texels: a shadow map that resolves nothing. Sizing it to the bulk
 * instead gives up on the outliers arbitrarily, and picks a percentile no artist authored.
 *
 * ⭐ SO THE BOX IS ANCHORED TO THE CAMERA, SNAPPED TO A GRID OF `refitM`, AND THE MAP RE-RENDERS
 * ONLY WHEN THE SNAPPED CENTRE MOVES. That resolves the tension honestly rather than by picking a
 * side:
 *   - It is never a picture of somewhere else: the box always contains the cascades' whole reach
 *     (`staticRadiusM = distanceM + refitM`, derived below rather than authored, so it cannot be
 *     set too small).
 *   - **A camera ROTATION never refits it**, because the anchor is the camera's POSITION. That is
 *     the case this level is worst at — the far cascade's draw count swings 677..1,099 with yaw —
 *     and it becomes free rather than cheaper.
 *   - Standing still costs zero, which is what an artist dialling a light in is doing.
 *   - Walking costs ONE full static pass every `refitM` metres. At 7 m/s and `refitM` 24 that is
 *     one extra pass every ~3.4 s, against ~20 ms saved on each of the ~200 frames between them.
 *     The hitch is REPORTED (`CsmState.split.refits`), never assumed away.
 *
 * ── ⚠️ WHAT A STALE STATIC MAP LOOKS LIKE, AND WHICH KIND OF STALENESS IS POSSIBLE ─────────────
 *
 * Two kinds, and only one of them can happen here.
 *
 * **Stale POSITION — structurally impossible.** The box always contains the cascades' whole reach
 * plus one refit cell (`staticRadiusM = distanceM + refitM`), and the anchor is snapped to a grid
 * of exactly `refitM`. So the camera cannot leave the region the map covers without the snapped
 * anchor moving, which is the refit trigger. There is no "correct at the freeze point, draining
 * away as you walk" failure — the mode `detachForBake` exists to prevent, and the reason
 * `sunShadowPolicy` refuses to cascade a `staticOnly` sun.
 *
 * **Stale CONTENT — possible, and this is the artefact.** The map holds the static world as it was
 * at the last refit. A `static_mesh` whose 50 MB glTF lands after it, an entity spawned by
 * gameplay, or a wall an artist moves in the editor casts the shadow it had — or none — until
 * something calls `csmRig.refreshSunShadow()`. On screen that is a shadow in the wrong place or a
 * missing one, in the frozen band only, and it does not correct itself by walking. The refit walk
 * re-runs `enableStaticShadowLayer` for exactly that reason, and `refreshSunShadow` is the hook the
 * editor and the world loader already call when they know the world moved.
 *
 * ── ⚠️ THE QUALITY COST, WHICH IS REAL AND IS THE OWNER'S CALL ──────────────────────────────────
 *
 * The static map has ONE texel density over `2 × staticRadiusM`, where the near cascade has its
 * own over a 10 m slice. At tier 2 (2048², reach 90 m, refit 24) that is **228 m / 2048 ≈ 11 cm**
 * against the near cascade's **10 m / 2048 ≈ 0.5 cm** — 22× coarser for STATIC geometry's own
 * contact shadows. `sharedCascades` is the dial that buys it back: the first N cascades keep layer
 * 0 (both channels, i.e. exactly today's picture and today's cost), and only the cascades beyond
 * them go dynamic-only. `1` is the default because cascade 0 is the cheapest of the three
 * (5.6 ms of 30.3) and it is the one covering everything the player is standing next to.
 *
 * ⭐ **IT IS DEFAULT-ON AS `far` SINCE THE 2.2x MEASUREMENT, AND `far` IS THE ARM WHOSE COST IS
 * SMALLEST.** It freezes only cascades beyond the FIRST, so the near field is untouched and the
 * band that coarsens is the one where texel density matters least: 1.25x over 28-90 m at tier 2,
 * identical inside 28 m. Tiers 0 and 1 run one cascade and therefore get NO split at all — the
 * arm resolves off rather than freezing their only cascade (see the resolver).
 *
 * ⚠️ The trade is still a picture and the owner still gets to veto it in ONE flag: `?shadowSplit=off`
 * restores today's rig exactly, and it is the control arm every shadow measurement should be taken
 * against. The reason it ships on rather than waiting for him is a number he asked for — the frame
 * on the level he is shipping goes 133.1 → 60.5 ms — not a judgement about the picture.
 */
export interface SunShadowSplit {
  readonly enabled: boolean;
  /** Edge of the static map, texels. 0 when disabled. */
  readonly staticMapSize: number;
  /**
   * Half-extent of the static ortho box, metres. DERIVED as `distanceM + refitM`, never authored:
   * a static map narrower than the cascades' reach would leave a ring in which static geometry
   * casts no shadow at all, and nothing on screen would name the cause.
   */
  readonly staticRadiusM: number;
  /** The snap grid the box centre is quantised to; also the slack the radius carries. */
  readonly refitM: number;
  /**
   * How many NEAR cascades keep layer 0 — i.e. render both channels, exactly as today. The
   * cascades beyond them render movers only. `0` is the maximum saving and the coarsest static
   * contact shadows; `cascades` is a no-op (and is what makes the arm safe to clamp into).
   */
  readonly sharedCascades: number;
  /**
   * ⭐ THE REACH, STATED IN METRES: static shadows come from the frozen map from here out to
   * `staticRadiusM`, and from the live cascades nearer than it. This exists because the owner's
   * objection to precomputed shadows was that they *"[force] them right out to the whole scene"* —
   * so the band the frozen map owns must be a number on the readout, not something inferred from a
   * cascade index.
   */
  readonly frozenFromM: number;
}

export const SUN_SHADOW_SPLIT_OFF: SunShadowSplit = {
  enabled: false,
  staticMapSize: 0,
  staticRadiusM: 0,
  refitM: 0,
  sharedCascades: 0,
  frozenFromM: 0,
};

/**
 * The `?shadowSplit=` vocabulary. **`null` ⇒ `far`, which is the shipped default**; `'off'` is the
 * diagnostic control arm and is a choice, never the absence of one — see `resolveSunShadowSplit`.
 *
 * ⭐ THE ARMS ARE ORDERED BY HOW MUCH OF THE CASCADE LADDER THEY FREEZE, NEAREST LAST, and each is
 * a different point on the one trade — because texel density and cost both fall off with distance,
 * so freezing the FAR field is nearly free and freezing the NEAR field is not. Measured on
 * `mars-hangar-01` at tier 2 (breaks 10 / 28 / 90 m, maps 2048/1024/1024):
 *
 *   `far`   — freeze only the LAST cascade. It is 1,038 of the 1,668 shadow draws and 15–31 ms of
 *             the 36–61 ms; the static map replacing it is 11.1 cm/texel against that cascade's own
 *             ~8.8 cm, i.e. **1.25× coarser over 28–90 m and identical everywhere nearer**. The
 *             best value in the whole design and the one to put in front of an artist first.
 *   `near`  — keep only cascade 0 live (0–10 m untouched). 10–28 m degrades from ~2.7 cm to 11.1 cm
 *             (4×), 28–90 m is the 1.25× above.
 *   `all`   — nothing static is live. Maximum saving, and static contact shadows are 11.1 cm
 *             everywhere including under the player's feet.
 */
export type SunShadowSplitMode = 'off' | 'far' | 'near' | 'all';

export const SUN_SHADOW_SPLIT_MODES: readonly SunShadowSplitMode[] = ['off', 'far', 'near', 'all'];

/**
 * ⭐ THE SHIPPED DEFAULT, AS A NAMED CONSTANT. Every reader of "what happens when nobody asked"
 * goes through this — the resolver, the tests, and anyone changing it later, who then changes it in
 * ONE place and finds every pin that quotes it.
 */
export const SUN_SHADOW_SPLIT_DEFAULT: SunShadowSplitMode = 'far';

/**
 * ── ⭐ THE MAPS A LIGHT'S SHADOW TERM ACTUALLY RENDERS, PUBLISHED ON THE `LightShadow` ───────────
 *
 * `vramAccountant.shadowBytesOf` has to answer *"how many bytes of shadow map does this light
 * allocate"*, and under a custom `shadow.shadowNode` the light's OWN map is never allocated at all
 * — the maps that exist belong to objects the graph walk cannot see as lights. It solved that by
 * duck-typing **into three's `CSMShadowNode`**: `shadow.shadowNode.lights[]`.
 *
 * ⚠️ THAT REACH IS ALREADY BROKEN ON `main`, AND NOT BY THE SPLIT. The moment the slot holds a
 * COMPOSITION rather than the bare node it has no `.lights`, and the census silently falls through
 * to pricing the sun's own unallocated map — so **every world carrying the baked per-probe sun term
 * has been mispricing its shadow VRAM since that feature landed** (2048² = 33.55 MB reported
 * against 50.33 MB of cascades actually allocated). The split would have made it worse, not caused
 * it. This is the fix for both, and it is the module's own stated rule applied: an under-stating
 * budget instrument is the failure this repo has already had once.
 *
 * `csmRig` is the ONE writer — it already owns `sunShadowMaps()`, the single authority on which
 * `LightShadow`s the sun's frame renders — and the census READS this in preference to guessing at a
 * vendor node's internals. The `shadowNode.lights` reach stays as the fallback, because a bare
 * `CSMShadowNode` put there by anything else is still priced correctly.
 */
export const FWG_DRIVEN_SHADOW_MAPS = 'fwgDrivenShadowMaps';

/** Metres of slack, and the grid the static box's centre snaps to. */
export const SUN_SHADOW_SPLIT_REFIT_M = 24;

/**
 * ⭐ THE ONE RESOLVER, so the rig, the pricing and the state readout cannot disagree about what an
 * arm means. `mode` is the selection; everything else is DERIVED from the cascade policy that is
 * already resolved, which is what keeps the static box from being independently mis-sized.
 */
export function resolveSunShadowSplit(
  mode: SunShadowSplitMode | null,
  cascades: number,
  distanceM: number,
  nearMapSize: number,
  refitM: number = SUN_SHADOW_SPLIT_REFIT_M,
  breaksM: readonly number[] = [],
): SunShadowSplit {
  /*
   * ── ⭐ `null` IS "NO PREFERENCE" AND RESOLVES TO `far`; `'off'` IS A CHOICE AND STAYS REACHABLE ─
   *
   * These were the same value until now, and separating them is the whole of this change. `null`
   * means the session expressed no preference and takes the SHIPPED DEFAULT; `'off'` means someone
   * asked for no split, and it is the diagnostic CONTROL ARM every future shadow measurement is
   * taken against. It is not deprecated and must never become a synonym for absence again.
   *
   * The precedent is `world/schema.resolveGiAdaptive`, which is this shape exactly: absent and `{}`
   * resolve ON, and only an explicit `enabled: false` takes the uniform arm — default-on RESOLVED
   * IN CODE rather than defaulted in a schema, because a schema default would materialise into
   * every parse. Nothing here is authored or hashed either: `SunShadowSplit` is derived per session
   * from the cascade policy, so this changes no world byte and no `worldHash`.
   *
   * ⭐ MEASURED, and this is why the default moves. The coordinator, current tip, same script and
   * camera minutes apart: **median 133.1 ms → 60.5 ms, 868 → 609 draws, 16.14 → 11.54 M triangles
   * — 2.2x the frame.** Corroborated from a second instrument (`?csm=0` reads 75.5 ms with 3 passes
   * against 5, putting shadows at ~43 % of the frame, against the 42.7 % the research measured).
   *
   * ⚠️ AND THE COST, WHICH IS A PICTURE AND MUST BE THE FIRST THING A READER OF THIS DIFF SEES:
   * static shadows beyond the last LIVE break come from the frozen map at ~11.1 cm/texel against
   * that cascade's own ~8.8 cm — **1.25x coarser over 28-90 m at tier 2, and identical nearer** —
   * and a static object that moves after a refit keeps a stale shadow until something calls
   * `csmRig.refreshSunShadow()`. `?shadowSplit=off` is the one flag that reverts all of it.
   */
  // ONE place the default is spelled, and it is a NAMED arm rather than a fall-through: the first
  // draft let `null` reach a `? :` chain whose last branch was `all`, so "no preference" silently
  // took the MOST aggressive arm — enabled at tier 0, freezing its only cascade. A default that is
  // the tail of a ternary is a default nobody can see.
  const m: SunShadowSplitMode = mode ?? SUN_SHADOW_SPLIT_DEFAULT;
  if (m === 'off') return SUN_SHADOW_SPLIT_OFF;
  const refit = Number.isFinite(refitM) && refitM > 0 ? refitM : SUN_SHADOW_SPLIT_REFIT_M;
  /*
   * ⭐ `far` NEVER FREEZES THE NEAR CASCADE — `max(1, …)`, NOT `max(0, …)`, AND THE TIER TABLE IS
   * WHY. Tiers 0 and 1 run ONE cascade, so `cascades - 1` is 0 there and a naive `far` would freeze
   * the only cascade there is — i.e. become `all` — coarsening the NEAR field from 40 m/1024
   * (~3.9 cm) to 128 m/1024 (~12.5 cm), a 3.2x regression in the first 40 m, on the weakest devices
   * this project has (the school tablets `lightModel` names as the audience), while doubling their
   * shadow VRAM. `far` means "freeze the cascades BEYOND the first"; where there is no such cascade
   * it means nothing, and the arm below says so in bytes rather than in a comment.
   */
  const shared = Math.max(
    0,
    Math.min(cascades, m === 'far' ? Math.max(1, cascades - 1) : m === 'near' ? 1 : 0),
  );
  /*
   * ⛔ A SPLIT THAT FREEZES NOTHING IS REFUSED RATHER THAN ALLOCATED. With every cascade live the
   * static map would be sampled through a `min` against terms that already describe the same
   * geometry — no draw removed, no pass removed, and 8-34 MB of shadow VRAM plus one full static
   * pass at load for a picture identical to `off`. That is the "compiled, costs, does nothing"
   * shape, so it resolves OFF. It is what a single-cascade tier gets from the default, and what
   * `?csm=1&shadowSplit=near` gets — coherent, not an error.
   */
  if (shared >= cascades) return SUN_SHADOW_SPLIT_OFF;
  return {
    enabled: true,
    staticMapSize: nearMapSize,
    /*
     * ⭐ OWNER REQUIREMENT — `shadowDistanceM` BOUNDS THE PRECOMPUTED MAP, AND IT IS THE ONLY THING
     * THAT SIZES IT. The reach is the RESOLVED cascade distance plus one refit cell of slack, and
     * nothing here reads the level's extent, `drawnBounds`, the GI volume or `shadowExtentM`. On
     * `mars-hangar-01` that is 90 + 24 = **114 m** — against placements spanning 3,921 × 1,574 ×
     * 3,416 m, i.e. a box 19× smaller in radius than the level and 20× sharper per texel than one
     * fitted to it would be. Lower the authored `shadowDistanceM` and this follows it down, which
     * is exactly the control the owner asked to keep working.
     */
    staticRadiusM: distanceM + refit,
    refitM: refit,
    sharedCascades: shared,
    /*
     * WHERE THE FROZEN MAP TAKES OVER, in metres, so the reach is a NUMBER an artist can read
     * rather than a cascade index they have to decode. 0 means "from the camera outward" (`all`);
     * otherwise it is the last live cascade's break. `staticRadiusM` is where it stops.
     */
    frozenFromM: shared > 0 ? (breaksM[shared - 1] ?? 0) : 0,
  };
}

/** Lenient, never throws — a stale URL or a typo takes the shipped arm and says so once. */
export function parseSunShadowSplitMode(raw: string | null): SunShadowSplitMode | null {
  if (raw === null) return null;
  const v = raw.trim().toLowerCase();
  if (v === '1' || v === 'on') return 'near';
  if (v === '0') return 'off';
  return (SUN_SHADOW_SPLIT_MODES as readonly string[]).includes(v)
    ? (v as SunShadowSplitMode)
    : null;
}

/**
 * ── ⭐ `radiusM` IS TWO FIELDS WEARING ONE NAME — RESOLVED HERE, ONCE, FOR EVERY CONSUMER ───────
 *
 * L1 fixed the ARTIST'S half with per-type labels (`Attenuation radius (m)` vs `Shadow extent
 * (m)`). This is the CODE'S half: two named functions, so `worldLights.update()`, the cost model
 * and any future culler ask the same question of the same field instead of each re-deriving "what
 * does radiusM mean for this type".
 *
 * ⚠️ L2 WAS SPECIFIED TO ADD `attenuationRadiusM` AND `shadowExtentM` AS ALIAS FIELDS AND
 * DELIBERATELY DOES NOT. That would leave one lighting block with THREE names for TWO meanings
 * plus a precedence rule between them — one more answer to a question that already had two. The
 * defect the additive rename aimed at is that a reader cannot tell which meaning is in play, and a
 * label plus a named function both fix that at zero `worldHash` cost.
 *
 * `null` means UNREPRESENTABLE, not merely unused, and for a directional light's attenuation that
 * is literal — `DirectionalLightNode.setupDirect()` in r185 is three lines:
 *
 *     setupDirect() {
 *         const lightColor = this.colorNode;
 *         const lightDirection = lightTargetDirection( this.light );
 *         return { lightDirection, lightColor };
 *     }
 *
 * with no `getDistanceAttenuation` call at all. There is no distance term in the shader to
 * attenuate, which is also why a "fade this light out with distance" field would have nothing to
 * write to on a directional light — see `FADE_DISTANCE_REFUSAL` below.
 */
export interface LightRadiusInput {
  readonly type: LightType;
  readonly radiusM: number;
}

/** three's `light.distance` (the attenuation cutoff) — point and spot only. */
export function lightDistanceM(cfg: LightRadiusInput): number | null {
  return cfg.type === 'point' || cfg.type === 'spot' ? cfg.radiusM : null;
}

/** The orthographic HALF-EXTENT of a shadow camera — directional only, the sun's `shadowExtentM`. */
export function lightShadowExtentM(cfg: LightRadiusInput): number | null {
  return cfg.type === 'directional' ? cfg.radiusM : null;
}

/**
 * ── ⚠️ FADE DISTANCE IS REFUSED ON THIS PATH, AND THE FIELD IS NOT AUTHORED ─────────────────────
 *
 * The owner asked for "fade distance, attenuation radius (N/A for directional), etc." Attenuation
 * radius exists, is applied and is free. Fade distance is refused, and the reason is that under
 * the stock `Lighting` system there are exactly two mechanisms and both are traps:
 *
 *  1. `light.visible = false` — `Renderer._projectObject` returns early on `visible === false`, so
 *     the light never reaches `pushLight`, vanishes from `_lights`, and DISAPPEARS FROM
 *     `LightsNode.customCacheKey()`. A per-frame visibility toggle is a per-frame full-scene WGSL
 *     regeneration: the single worst thing a camera walk could do to this renderer.
 *  2. `intensity → 0` — `src/render/indirect.ts` already forbids this as a presence mechanism. The
 *     light stays in the loop at full price and it pops.
 *
 * Measured, the field would also buy nothing even if it were free: `cutoffDistanceNode` feeds a
 * `select`, so BOTH ARMS RUN, and stock/huge vs stock/local on Sponza is 3.12 vs 3.06 ms/light —
 * noise. Culling only exists under `ClusteredLighting`, which D10 rejects and which SILENTLY DOES
 * NOT LIGHT on tier 0.
 *
 * So the field is not authored. An inert authored field is precisely the failure this repo has
 * thirteen logged instances of, and "author it in L2, implement it in L3" would have shipped one.
 * When the batched path lands (`DynamicLightsNode` hashes only the TYPE SET and drives its loop
 * from a `uniform(0,'int')` count, so membership is a uniform write), the field and its
 * implementation land together.
 */
export const FADE_DISTANCE_REFUSAL =
  'Fade distance is not authored on this path. Under three’s stock lighting the only ways to drop ' +
  'a light are light.visible (which is in every material’s pipeline cache key — toggling it per ' +
  'frame regenerates every visible shader) and intensity 0 (which still costs the full per-frame ' +
  'price and pops). Attenuation radius is the field that exists and is applied.';

/**
 * ── ⭐ SHADOW VRAM — THE LARGEST HOLE THE CENSUS DECLARED IT COULD NOT SEE ──────────────────────
 *
 * `censusScene`'s `excludes` carried `'shadow-maps'` from the day it was written, and R1.5-E
 * measured why that mattered: **~33 MB for the sun's 2048² alone**, on a budget instrument that
 * reported the geometry and textures beside it to the byte. It is pure arithmetic over authored
 * data, so there was never a reason for it to be a hole.
 *
 * PER TEXEL, from the r185 allocation rather than a rule of thumb —
 * `ShadowNode.setupRenderTarget`:
 *
 *     const depthTexture = new DepthTexture( w, h );          // UnsignedIntType   → 4 B
 *     const shadowMap = builder.createRenderTarget( w, h );   // RGBA              → 4 B
 *     shadowMap.texture.type = shadow.mapType;                // UnsignedByteType (LightShadow.js:112)
 *
 * i.e. three's WebGPU path allocates a full RGBA COLOUR ATTACHMENT beside every depth texture.
 * That is the half everyone's estimate misses, and it is what makes the number twice what people
 * expect. 8 B/texel, ×6 for a point light, whose `PointShadowNode` allocates a CUBE target
 * (`createCubeRenderTarget( size )`, and note it passes `size` for BOTH axes — a non-square point
 * shadow map is silently squared).
 *
 * ⚠️ IT PRICES `mapSize`, NOT AN ALLOCATION, and that is deliberate. The target is created lazily
 * by `ShadowNode.setup()` on the light's first render, so on frame 0 this over-states by exactly
 * the maps that are about to exist. An UNDER-stating budget instrument is the failure that
 * matters here — the accrual ledger reported 0.0 MB for a 68-texture scene — so it prices what
 * will be there rather than what already is.
 */
export const SHADOW_MAP_BYTES_PER_TEXEL = 8;

export function shadowMapBytes(input: {
  type: LightType;
  castShadow: boolean;
  presence: LightPresence;
  shadowMapSize: number;
}): number {
  /*
   * A refused light allocates nothing. A `static` (bake-presence) light DOES: the bake bracket
   * parents it and `freezeSceneForBake` renders its map once per bake, so the memory is real — it
   * is simply not resident every frame. Charging it is what stops "make it static" reading as a
   * free way to add shadow casters.
   */
  if (!input.castShadow || input.presence === 'none') return 0;
  const faces = input.type === 'point' ? POINT_SHADOW_FACES : 1;
  return input.shadowMapSize * input.shadowMapSize * SHADOW_MAP_BYTES_PER_TEXEL * faces;
}

/**
 * ── ⭐ L2's CACHE-KEY DISCIPLINE, AS DATA ───────────────────────────────────────────────────────
 *
 * `LightsNode.customCacheKey()` (r185, `LightsNode.js:143-166`) pushes, per light: `light.id`,
 * `light.castShadow ? 1 : 0`, and for spots `light.map?.id ?? -1` and
 * `light.colorNode?.getCacheKey() ?? -1`. `RenderObject.needsUpdate` compares the folded dynamic
 * key with the one the object compiled against and DISPOSES AND REBUILDS on a mismatch — a fresh
 * NodeBuilder run and new WGSL for every visible material.
 *
 * So the owner's *"art direct them"* is either free or catastrophic depending entirely on which
 * field an editor writes, and the difference is not visible at the call site. These two lists are
 * that difference, verified field by field in the r185 node sources, and
 * `tests/renderLightShadowPolicy.test.ts` asserts `keyMoves === 0` across every FREE field and
 * `>= 1` across every KEY field.
 *
 * ⚠️ `visible` IS IN THE SECOND LIST AND IT SURPRISES EVERYONE. It is not in `customCacheKey()`
 * itself — it is worse: `_projectObject` returns before `pushLight`, so the light is not in the
 * array the key is computed from, which is exactly as expensive as deleting it.
 */
export const LIGHT_KEY_FREE_FIELDS = [
  // AnalyticLightNode.update: color.copy(light.color).multiplyScalar(light.intensity)
  'color',
  'intensity',
  // pose — matrixWorld, read per frame by the light node's own uniforms
  'position',
  // PointLightNode/SpotLightNode.update → cutoffDistanceNode / decayExponentNode (renderGroup)
  'distance',
  'decay',
  // SpotLightNode.update → coneCosNode / penumbraCosNode (renderGroup)
  'angle',
  'penumbra',
  // LightShadow fields: read by ShadowNode per pass, in no key
  'shadow.bias',
  'shadow.radius',
  'shadow.intensity',
  'shadow.mapSize',
  'shadow.autoUpdate',
  'shadow.needsUpdate',
  'shadow.camera.layers',
] as const;

export const LIGHT_KEY_MOVING_FIELDS = [
  'castShadow', // LightsNode.customCacheKey pushes it per light
  'visible', // _projectObject returns early ⇒ the light leaves the array the key is built from
  'type', // a different constructor is a different object is a different id
  'mobility', // across the runtime/bake line it is a construction or a removal
] as const;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ── ⭐ B3 · RECEIVERS — ONE DECLARATION PER CLASS, AND WHY A FLAG IS NOT A CALL-SITE DECISION ──
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The owner's report: *"the character (and possibly other moveable items) don't look like they're
 * receiving shadows from the static meshes."* The bug was one missing property — the player body
 * was `castShadow = true` and never `receiveShadow` — and under the node path that is not a
 * cosmetic default. `AnalyticLightNode.setup()` (three r185, `AnalyticLightNode.js`) reads:
 *
 *     if ( this.light.castShadow ) {
 *         if ( builder.object.receiveShadow ) {
 *             this.setupShadow( builder );
 *
 * so a receiver without the flag has NO shadow term compiled into its lighting graph at all. It is
 * not "lit through a map that happens to be empty"; the fetch does not exist. And `RenderObject`
 * hashes `object.receiveShadow` into its cache key (`RenderObject.js:843`), so the flag is a
 * per-object PROGRAM VARIANT that must be set once at construction, never per frame.
 *
 * The system-shaped defect was that nothing DECLARED what a class does with shadows:
 * `castShadow`/`receiveShadow` were written at nineteen sites across nine files, so a missing
 * line could not be caught because nothing said what the line should be. This table is that
 * declaration for the objects `scene.ts` constructs itself; registry entity classes declare
 * theirs in `EntityMeta.shadow` beside `category`, and `render/lights/shadowFlags.ts` is the ONE
 * applier (a subtree walk plus a `childadded` hook, because the real character mesh and the
 * companion rig both arrive under their groups seconds after the groups exist). A scan in
 * `tests/renderShadowReceivers.test.ts` refuses a bare mesh `castShadow =`/`receiveShadow =`
 * anywhere else, which is what turns the owner's report into a class of bug that cannot recur.
 *
 * `movable` is the third column because it is the same decision: it is what
 * `markDynamicShadowCaster` marks — a subtree that must never be burned into a FROZEN shadow map
 * (`shadowUpdate: 'staticOnly'`). Declaring it beside cast/receive is what keeps "the player
 * receives" and "the player is not in a frozen map" from being two facts in two files.
 */
export interface ShadowFlags {
  /** Rendered into shadow maps. ⚠️ On a material with a custom `colorNode` this THROWS (see the companion row). */
  readonly cast: boolean;
  /** Compiles the shadow fetch into this object's lighting graph. A per-object program variant. */
  readonly receive: boolean;
  /** Re-posed at runtime ⇒ excluded from frozen (`staticOnly`) shadow maps as a whole subtree. */
  readonly movable: boolean;
}

export const SHADOW_FLAGS = {
  /*
   * There was a `ground` row here — "the 240 m plane, nothing is under it to cast onto" — and it
   * is gone with the plane it described. The floor is a `world.boxes` entry now, so it rides
   * `gymBoxes` below and CASTS as well as receives, which is correct for a box with a thickness
   * and other geometry beside it. A flags row whose object no longer exists is a declaration
   * nothing enforces, and this table is only worth reading because every row has a caller.
   */
  /** The gym's one InstancedMesh of boxes — static world geometry, the FLOOR included. */
  gymBoxes: { cast: true, receive: true, movable: false },
  /** Moving platforms: one InstancedMesh re-posed every frame. */
  platforms: { cast: true, receive: true, movable: true },
  /**
   * The player: capsule + nose at construction, the glTF character under `rollSpin` seconds later.
   * `receive: true` IS the owner's fix — the one property that was never written.
   */
  player: { cast: true, receive: true, movable: true },
  /**
   * The companion, hull and rig alike. `cast: false` is MEASURED, not an omission (three r185,
   * `docs/ai/tsl-patterns.ts` pattern 3): `Renderer._getShadowNodes` pulls any material with a
   * custom `colorNode` into the shadow pass to read its alpha, and the pass renders through three's
   * shared `ShadowMaterial`, where every `materialReference` in the factory graph resolves to
   * `undefined` and `TextureNode.setup()` throws. `receive` only changes the LIT graph
   * (`setupShadow`), never the shadow pass, so he can receive without tripping it.
   */
  companion: { cast: false, receive: true, movable: true },
  /** Editor light/volume sprites: `SpriteNodeMaterial` is unlit. Asserted, never applied. */
  editorSprites: { cast: false, receive: false, movable: true },
} as const satisfies Record<string, ShadowFlags>;

/**
 * What an object an entity parents to `ctx.root` OUTSIDE its `create()` gets — pot shards on a
 * break, the anchor's crate drawn at world start. The host cannot attribute those to a class (they
 * arrive from a callback, not from the factory), so they take the conservative answer: they cast,
 * they receive, and they are never burned into a frozen map. The one class this would be WRONG for
 * is the companion (`cast` throws on his materials), and his rig lands under his own root inside
 * `create()`, where the class column wins.
 */
export const ENTITY_SHADOW_DEFAULT: ShadowFlags = { cast: true, receive: true, movable: true };

/**
 * The vocabulary of `EntityMeta.shadow` — the registry's column, declared here so the eager
 * registry table and the exploration sub-table import one PURE module rather than each other
 * (`table.ts` may only type-import `registry.ts`; a value import would be a cycle). Four answers
 * cover every class that exists: a PROP, a GHOST (editor-only marker, hidden in play), a MOVING
 * prop (the pot: its shards fly) and the COMPANION (`SHADOW_FLAGS.companion`'s measured reason).
 */
export const ENTITY_SHADOW = {
  prop: { cast: true, receive: true, movable: false },
  ghost: { cast: false, receive: false, movable: false },
  movingProp: { cast: true, receive: true, movable: true },
  companion: SHADOW_FLAGS.companion,
} as const satisfies Record<string, ShadowFlags>;

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ── ⭐ B3 · THE SUN'S SHADOW IS CASCADED, AND THE POLICY IS TIER-SIDE DATA ──────────────────────
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The owner's *"given distances and moving cameras"*. The sun's shadow was ONE orthographic map,
 * `±shadowExtentM` about the WORLD ORIGIN (`applySun`), 2.9 cm texels over 60 m at Sponza and
 * NOTHING shadowed outside the box — `ShadowNode.js`'s `frustumTest.select( shadowNode, float( 1 ) )`
 * returns LIT beyond the frustum. It did not follow the camera. Cascaded shadow maps are the fix,
 * and r185 ships them for the node path: `examples/jsm/csm/CSMShadowNode.js` plugs in through the
 * hook `AnalyticLightNode.setupShadow` reads —
 *
 *     const customShadowNode = this.light.shadow.shadowNode;
 *     if ( customShadowNode !== undefined ) { shadowNode = nodeObject( customShadowNode ); }
 *     else { shadowNode = this.setupShadowNode(); }
 *
 * — so the sun's OWN map is never built (N cascades = N depth passes, not N + 1), and every
 * material receives without opting in (`ShadowBaseNode.setupShadowPosition` reads the material's
 * `receivedShadowPositionNode || positionWorld`). Per frame the node re-fits each cascade's ortho
 * box to the camera-frustum slice in light space, texel-snapped
 * (`Math.floor( _center.x / texelWidth ) * texelWidth`), which is "follows the camera without
 * swimming". The mechanism is `render/lights/csmRig.ts`; THIS is the table it reads.
 *
 * ── WHY THE POLICY IS HERE AND NOT IN `render.json` ─────────────────────────────────────────────
 *
 * Same reason as the cost table above it: `world/schema.ts` imports this file and
 * `tools/bundler/bake-world.ts` imports `world/schema.ts` and runs UNBUNDLED under
 * `node --experimental-strip-types`, which adds no import attribute for JSON. Pure data here is
 * asserted in `npm run check` with no GPU and reachable from the baker's chain.
 *
 * ── THE TABLE, PRICED AGAINST THE FRAME-COST LINE ───────────────────────────────────────────────
 *
 *   tier   cascades   breaks (m)     maps                  fade   traversals   VRAM
 *   2      3          [10, 28, 90]   2048 / 1024 / 1024    on     3            50.3 MB (33.55 before)
 *   1      1          [40]           1024                  off    1             8.4 MB (33.55 before)
 *   0      1          [40]           1024                  off    1             8.4 MB
 *
 * Cascade 0's slice at tier 2 has a ~21 m diagonal at 2048 ⇒ ~1.0 cm texels (vs 2.9 cm today, and
 * it follows the camera); cascade 2 covers 30–90 m at ~18 cm, where the probe volume carries the
 * low-frequency term anyway. `cascades: 1` IS the "single camera-fitted map" fallback — the same
 * class with one slice, not a second implementation — so tier 1 gets a 1024² map fitted to 40 m in
 * place of a 2048² box at the origin: sharper AND 4× less VRAM. Tier 2 spends 3 of its 4 shadow
 * traversals on the sun (one traversal is ≤ 2.96 ms on Sponza, `SHADOW_COST_NOTE`); each cascade
 * frustum-culls to its slice, so the measured cost is expected BELOW 3× one origin map — the
 * owner's GPU decides the number, this table decides the ceiling. Degrade order when the line
 * reads over budget: the far cascade to a lower refresh rate, then `cascades: 2`, then 1 —
 * REPORTED, never enforced, the standing rule.
 *
 * `fade` blends the last metres of each cascade into the next (`_setupFade`); it is baked into the
 * node graph at build, like the count, so both are session constants — changing either is a
 * rebuild, and `csmRig.apply` schedules it across two frames rather than doing it per edit.
 */
export interface SunShadowTierRow {
  readonly cascades: number;
  readonly distanceM: number;
  /** The near cascade's map is `shadowMapSizeFor(sun.shadowMapSize, cap, scale)`; others are half. */
  readonly mapSizeCap: number;
  /**
   * ⭐ THE SAME QUESTION FOR EVERY NON-SUN LIGHT, ANSWERED IN THE SAME ROW (the owner's *"the
   * shadow map resolution should be a part of the rendering settings, not a local setting"*).
   *
   * `mapSizeCap` above covered the sun's near cascade and NOTHING ELSE for its whole life —
   * `worldLights.applyShadow` wrote `cfg.shadowMapSize` raw, so one authored 1024 point light
   * allocated 1024² x 8 B x 6 faces = 50.33 MB, exactly the whole tier-2 cascade set, at every
   * tier including the ones with a 1024 sun cap. This is the cap for those lights.
   *
   * ⚠️ PROVISIONAL AT TIERS 0/1, and said rather than implied: no tablet has ever been measured on
   * this project (`lightCostBudget`'s tier-1 row already carries `provisional: true` for the same
   * reason). 1024 / 512 / 512 is chosen so the local cap never exceeds the sun's, which is the one
   * relationship that is defensible without a device.
   */
  readonly localMapSizeCap: number;
  readonly fade: boolean;
}

export const SUN_SHADOW_TIERS: Readonly<Record<0 | 1 | 2, SunShadowTierRow>> = {
  0: { cascades: 1, distanceM: 40, mapSizeCap: 1024, localMapSizeCap: 512, fade: false },
  1: { cascades: 1, distanceM: 40, mapSizeCap: 1024, localMapSizeCap: 512, fade: false },
  2: { cascades: 3, distanceM: 90, mapSizeCap: 2048, localMapSizeCap: 1024, fade: true },
};

/**
 * ── ⭐ THE ONE RESOLVER FOR "HOW BIG IS THIS SHADOW MAP" (the owner's item 4) ────────────────────
 *
 * Three numbers meet here and they are three DIFFERENT KINDS of thing, which is why they stay
 * three numbers and are folded in exactly one place:
 *
 *   `requested` — the ARTIST'S ASK. `WorldLightSchema.shadowMapSize` / `WorldSunSchema.shadowMapSize`,
 *                 level data, in `worldHash`, and unchanged by any of this. It stops being what is
 *                 ALLOCATED and becomes what was asked for.
 *   `cap`       — a DEVICE FACT. `SUN_SHADOW_TIERS[tier].mapSizeCap` / `.localMapSizeCap`.
 *   `scale`     — a HUMAN PREFERENCE for THIS SESSION. The `shadowRes` render-settings row
 *                 (`auto | 1 | 0.5 | 0.25`), never persisted to a world file.
 *
 * Snapped DOWN to a power-of-two rung rather than left as `cap x scale`: a `RenderTarget.setSize`
 * to an odd size is legal but every mip/filter assumption in the shadow sampler is written for
 * powers of two, and a ladder is what makes `0.5 x 0.5` land on 512 rather than on 511.
 *
 * ⛔ IT IS NEVER CALLED FROM `applySun`. That write is the map the BAKE photographs
 * (`csmRig.detachForBake` restores the stock box and never rewrites `mapSize`), so a tier- or
 * session-scaled size there would make the baked atlas a function of the machine that baked it —
 * two artists, one `world.json`, two atlases, every `checkProvenance` green. Live writers only:
 * `sunShadowPolicy`'s cascaded arm and `worldLights.applyShadow`.
 */
export const SHADOW_MAP_SIZES: readonly number[] = [256, 512, 1024, 2048, 4096];

/** The floor, spelled: a 0.25x multiplier on a 512 cap would otherwise land at 128. */
export const SHADOW_MAP_SIZE_MIN = 256;

export function shadowMapSizeFor(requested: number, cap: number, scale: number): number {
  const req = Number.isFinite(requested) && requested > 0 ? requested : SHADOW_MAP_SIZE_MIN;
  const lim = Number.isFinite(cap) && cap > 0 ? cap : Infinity;
  const mul = Number.isFinite(scale) && scale > 0 ? scale : 1;
  const want = Math.min(req, lim) * mul;
  let out = SHADOW_MAP_SIZE_MIN;
  for (const rung of SHADOW_MAP_SIZES) if (rung <= want) out = rung;
  return out;
}

/**
 * Where the cascades split, as FRACTIONS of the shadow distance, per count. Fixed rather than
 * three's `'practical'` mode: that is `lerp(uniform, log, 0.5)` off `camera.near`, and with our
 * near of 0.1 m the log term collapses (0.1 · 900^(1/3) ≈ 0.97 m for the first split). Metres an
 * artist can read and a test can pin — `cascadeBreaksM(3, 90)` is `[10, 28, 90]`.
 */
export const CASCADE_SPLITS: Readonly<Record<number, readonly number[]>> = {
  1: [1],
  2: [14 / 90, 1],
  3: [10 / 90, 28 / 90, 1],
  4: [6 / 90, 16 / 90, 40 / 90, 1],
};

export const SUN_CASCADES_MAX = 4;

/**
 * How far past a slice's far edge (along the light) each cascade camera is posed, metres —
 * `CSMShadowNode.lightMargin`. Casters between the camera and the slice (a wall the sun passes
 * over before reaching the player) still project only if they are inside this. three's default is
 * 200; 100 covers a 12 m Sponza wall eight times over and keeps `cascadeCameraFarM` inside the
 * parent camera's 500 m far plane on every authored distance up to 100 m.
 */
export const CSM_LIGHT_MARGIN_M = 100;

/** Metres → fractions of `farM`, the shape `CSMShadowNode.customSplitsCallback` pushes. */
export function cascadeBreaks(breaksM: readonly number[], farM: number): number[] {
  return breaksM.map((m) => Math.min(1, m / farM));
}

/** The cumulative split distances, metres, for a cascade count over a shadow distance. */
export function cascadeBreaksM(cascades: number, distanceM: number): number[] {
  const fractions = CASCADE_SPLITS[cascades] ?? CASCADE_SPLITS[1]!;
  return fractions.map((f) => Math.round(f * distanceM * 100) / 100);
}

/**
 * The cascade camera's far plane. The light is posed `lightMargin` beyond the slice's far edge in
 * light space, and the slice's own depth is at most its diameter — under 2.6× the distance at our
 * 55° FOV and any aspect a window has. 4× is the round number above that, derived from the pose
 * so it cannot drift, exactly as `applySun` derives the parent's.
 */
export function cascadeCameraFarM(distanceM: number, lightMarginM = CSM_LIGHT_MARGIN_M): number {
  return lightMarginM + 4 * distanceM;
}

/** The sun fields the policy reads. `WorldSunConfig` satisfies it; four are optional and hash-free. */
export interface SunShadowInput {
  readonly castShadow: boolean;
  readonly shadowMapSize: number;
  /** 0 or absent ⇒ the tier decides. Authored on `WorldSunSchema` as `.optional()` — see there. */
  readonly cascades?: number | undefined;
  /** 0 or absent ⇒ the tier decides. Metres; the live frame's shadow reach. */
  readonly shadowDistanceM?: number | undefined;
  /** B6 — absent ⇒ `'everyFrame'`, which is what the sun did for its whole life. */
  readonly shadowUpdate?: AppliedShadowUpdate | undefined;
  /** B6 — 0/absent ⇒ `DEFAULT_SHADOW_UPDATE_N` (`normalizeShadowUpdateN`). */
  readonly shadowUpdateN?: number | undefined;
}

/**
 * ── ⭐ B6 · THE SUN'S SHADOW SCHEDULE, RESOLVED THROUGH THE ONE RESOLVER ─────────────────────────
 *
 * `WorldSunSchema.shadowUpdate` is `.optional()` with NO default (`WorldSunSchema` is
 * `prefault({})` and hashed — see the schema), so ABSENT has to mean today's behaviour exactly.
 * This is the one place that sentence is written down, and it still goes through
 * `effectiveShadowUpdate` rather than round the side of it: the sun is `stationary` with no slot,
 * which is the identity arm today, and routing it through the resolver is what stops a future
 * change to what an arm MEANS applying to every light except the one that costs the most passes.
 */
export function sunShadowUpdate(sun: SunShadowInput): AppliedShadowUpdate {
  return effectiveShadowUpdate(sun.shadowUpdate ?? 'everyFrame', 'stationary', 'n/a');
}

export interface SunShadowPolicy {
  /**
   * False ⇒ the sun keeps its single origin map (`applySun`'s box) and is priced as before. The
   * control arm — `?csm=0` in a dev boot — so the owner can A/B the cascades against the map he
   * has, and the arm every L1/L2 pin was taken on.
   */
  readonly enabled: boolean;
  readonly cascades: number;
  readonly distanceM: number;
  /** Cumulative split distances, metres; the last equals `distanceM`. */
  readonly breaksM: readonly number[];
  /** Per cascade, near to far. */
  readonly mapSizes: readonly number[];
  readonly fade: boolean;
  readonly lightMarginM: number;
  /** Depth passes per frame the sun costs under this policy. 0 when it casts no shadow. */
  readonly traversals: number;
  /** Shadow-map VRAM under this policy (`mapSize² × 8 B` per cascade). 0 when it casts no shadow. */
  readonly bytes: number;
  /** Which of the two numbers an artist can move came from the world file. */
  readonly authored: { readonly cascades: boolean; readonly distance: boolean };
  /**
   * ⭐ THE OTHER AUTHORITY, CARRIED RATHER THAN RE-LOOKED-UP. The panel has to say what `0` MEANS
   * ("Tier decides (3)"), and `cascades` above is the RESOLVED answer — equal to the file's number
   * whenever the file authored one, so it cannot answer that question. This is the tier's own row,
   * unresolved, so every consumer of the fold reads the same two authorities the fold read.
   *
   * It also carries the two map-size CAPS, which is what lets the frame-cost line say `tier cap
   * 2048 sun / 1024 local x 0.50` without a second tier lookup in the editor.
   */
  readonly tierRow: SunShadowTierRow;
  /** The session's `shadowRes` multiplier this policy's `mapSizes` were resolved under. */
  readonly resScale: number;
  /**
   * ⭐ B6 — HOW OFTEN THE SUN'S SHADOW MAP(S) RE-RENDER, resolved through `effectiveShadowUpdate`.
   *
   * It rides on the POLICY rather than being handed to `csmRig` as a fifth argument because the
   * policy is already the one object every consumer reads: `csmRig.apply` (which owns the cascade
   * `LightShadow` clones), `describeLightRig` (which prices the row), `localShadowBudget` (which
   * spends what the sun leaves) and the editor's resolved line all take it, so an apply cannot
   * carry a new reach and drop the schedule.
   */
  readonly shadowUpdate: AppliedShadowUpdate;
  /** N for the `everyNFrames` arm, already normalised (`normalizeShadowUpdateN`). */
  readonly shadowUpdateN: number;
  /**
   * ⭐ R5 — the two-map split. Rides on the policy for the same reason `shadowUpdate` does: the
   * policy is already the ONE object `csmRig.apply`, `describeLightRig` and `localShadowBudget`
   * all read, so an apply cannot carry a new reach and drop the split.
   *
   * ⚠️ IT MOVES `bytes` AND NOT `traversals`, AND THE ASYMMETRY IS THE MEASUREMENT. The static map
   * is real VRAM every frame, so it is in `bytes`. It is NOT a depth pass every frame — it renders
   * only when the camera's snapped anchor moves — so it adds nothing to the per-frame traversal
   * budget, exactly as a `staticOnly` caster contributes 0. What the split changes about the
   * cascades is how MUCH each pass draws, which `traversals` has never measured.
   */
  readonly split: SunShadowSplit;
}

/**
 * ⭐ THE ONE RESOLVER. The tier's row, overridden per field by the world's sun block when it says
 * something (a 0 says nothing — the inspector's number widget has no "absent", and 0 cascades is
 * not a shadow). `override` is the dev boot's `?csm=`: `0` disables (single map), `1..4` forces a
 * count. The count is clamped to `SUN_CASCADES_MAX` because `CASCADE_SPLITS` has no row past it.
 */
export function sunShadowPolicy(
  tier: number,
  sun: SunShadowInput,
  override: number | null = null,
  /**
   * ⭐ THE SESSION'S SHADOW-RESOLUTION MULTIPLIER (`renderSettings`'s `shadowRes` row). `1` — the
   * default every existing caller and every committed pin was taken on — resolves to exactly the
   * sizes this function has always returned, which is why it is a fourth optional argument and not
   * a field on `SunShadowInput` (that interface is satisfied by `WorldSunConfig`, i.e. by LEVEL
   * DATA, and a machine preference has no business in it).
   *
   * ⛔ IT DOES NOT REACH THE `single` ARM BELOW, deliberately. Under `?csm=0` the live sun map IS
   * `applySun`'s authored one — the same object the bake photographs — so capping it here would
   * report a size nothing wrote. `?csm=0` is the diagnostic control arm every L1/L2 number was
   * measured on and it stays uncapped; the `shadowRes` row's `note` says so, because a perf
   * number taken on that arm is then not comparable with one taken on the cascaded arm.
   */
  resScale = 1,
  /**
   * ⭐ R5 — the dev boot's `?shadowSplit=`. A FIFTH optional argument for the same reason `resScale`
   * is a fourth: `null` resolves to exactly the policy this function has always returned, so every
   * existing caller and every committed pin is byte-unmoved, and it is deliberately NOT a field on
   * `SunShadowInput` — that interface is satisfied by `WorldSunConfig`, i.e. by LEVEL DATA, and
   * this is a session selection an owner makes to look at a trade, not something a world authors.
   */
  splitMode: SunShadowSplitMode | null = null,
  /**
   * ⭐ R2 — a SESSION override of the sun's authored `shadowUpdate` / `shadowUpdateN`, for the same
   * reason `?csm=` overrides the cascade count: the arm is an owner trade and it has to be
   * A/B-able on a real frame before anyone authors it into a world.
   *
   * ⚠️ IT GOES THROUGH THE RESOLVER RATHER THAN BEING SPREAD OVER THE RESULT, and that is not
   * tidiness. `traversals` below is `amortiseShadowTraversals(cascades, update, updateN)` and
   * `localShadowBudget` spends what the sun leaves — so overriding the arm on the way OUT would
   * report three live traversals over a rig taking one, and the editor's frame-cost line would
   * lie in exactly the direction that makes a lever look free. One resolver, every consumer.
   */
  updateOverride: { update: AppliedShadowUpdate; n?: number } | null = null,
  /**
   * ⭐ A SESSION OVERRIDE OF THE CASCADE REACH — the dev boot's `?shadowDist=`, for exactly the
   * reason `?csm=` overrides the count: the owner has called the shipped 90 m "too much by a long
   * way", and the reach is the single strongest lever on shadow cost (the far cascade alone holds
   * 1,073 of 1,717 shadow draws on `mars-hangar-01` precisely because it covers 28–90 m). It has to
   * be A/B-able on a real frame before a number is authored into a world.
   *
   * ⚠️ IT IS NOT `world.json`, AND THAT IS DELIBERATE. Editing `sun.shadowDistanceM` moves
   * `worldHash`, which stales the atlas — and Mars's is a 3m48s bake and the first that world has
   * ever had. A boot override changes no authored byte, so a sweep costs nothing and refuses
   * nothing.
   *
   * `null` resolves to exactly the value this function has always returned. It also flows into the
   * frozen map's reach for free, because `resolveSunShadowSplit` takes the RESOLVED distance —
   * which is the owner's "precomputed shadows must obey shadow distance" requirement, held by
   * construction rather than by a second read.
   */
  distanceOverride: number | null = null,
): SunShadowPolicy {
  const row = SUN_SHADOW_TIERS[(Math.max(0, Math.min(2, Math.floor(tier))) as 0 | 1 | 2)];
  const update = updateOverride?.update ?? sunShadowUpdate(sun);
  const updateN = normalizeShadowUpdateN(updateOverride?.n ?? sun.shadowUpdateN);
  const single: SunShadowPolicy = {
    enabled: false,
    cascades: 0,
    distanceM: 0,
    breaksM: [],
    mapSizes: sun.castShadow ? [sun.shadowMapSize] : [],
    fade: false,
    lightMarginM: CSM_LIGHT_MARGIN_M,
    traversals: shadowTraversals('directional', sun.castShadow, 'runtime', update, updateN),
    bytes: shadowMapBytes({
      type: 'directional',
      castShadow: sun.castShadow,
      presence: 'runtime',
      shadowMapSize: sun.shadowMapSize,
    }),
    authored: { cascades: false, distance: false },
    tierRow: row,
    // ⛔ 1, NOT `resScale`, AND THAT IS THE `?csm=0` RULING SPELLED IN THE DATA: the single map is
    // `applySun`'s authored one — the map the bake photographs — so nothing scaled it.
    resScale: 1,
    shadowUpdate: update,
    shadowUpdateN: updateN,
    /*
     * ⛔ R5: THE SPLIT IS OFF ON THE SINGLE-MAP ARM, AND IT IS A REFUSAL WITH A REASON. `?csm=0`
     * and a `staticOnly` sun both resolve here, and both already ARE a world-anchored single map —
     * `staticOnly` is literally "the static channel, frozen". Composing a second static map beside
     * them would be a second answer to the question that arm exists to answer, in the one arm kept
     * as the diagnostic control. `csmRig` owns no cascade node here either, so there is nothing to
     * take the `min` against.
     */
    split: SUN_SHADOW_SPLIT_OFF,
  };
  if (override === 0) return single;
  /*
   * ── ⭐ B6 · A FROZEN SUN SHADOW IS NOT CASCADED, AND THE ARGUMENT IS `detachForBake`'s ─────────
   *
   * `staticOnly` means "render this map ONCE and never again". A `CSMShadowNode` cascade box is
   * FITTED TO THE GAME CAMERA'S FRUSTUM SLICE every frame (`CSMShadowNode.updateBefore` re-fits
   * and texel-snaps `lwLight.position` and `shadowCam.left/right/top/bottom`), so freezing the
   * PASS while the box keeps moving gives a map that is a picture of where the camera USED to be,
   * sampled through the matrix of that moment: shadows would be correct at the freeze point and
   * would silently drain away as the player walked out of the frozen slice. That is the exact
   * failure `csmRig.detachForBake` already exists to prevent — it puts this same world-anchored
   * ±`shadowExtentM` box back for the bake because "a capture 40 m from the frozen camera would
   * photograph the sun through walls". A frozen frame is 40 m from wherever you now are.
   *
   * So the arm is RESOLVED, not refused: `staticOnly` selects the single WORLD-ANCHORED map, which
   * is the only sun shadow in this codebase that can honestly be frozen. Every consumer reads it
   * off the policy (`enabled: false`), so csmRig never attaches, the editor's resolved line says
   * "single origin map" and `describeSunShadowPolicy` says WHY. ⚠️ The costs are real and are
   * reported, never hidden: the reach becomes `shadowExtentM` about the ORIGIN instead of the
   * cascades' camera-following distance, and there is no last cascade to hand over to, so the
   * baked per-probe distant sun term (`csmRig.setDistantShadowTerm`) is not installed — the same
   * refusal `?csm=0` already carries, for the same reason.
   *
   * ⛔ AND IT OUTRANKS `?csm=1..4`. The override moves the cascade COUNT; it is a diagnostic, and
   * letting it force cascades onto a frozen sun would re-create the defect this rule removes.
   */
  if (update === 'staticOnly') return single;
  const authoredCascades = sun.cascades !== undefined && sun.cascades > 0;
  const authoredDistance = sun.shadowDistanceM !== undefined && sun.shadowDistanceM > 0;
  const cascades = Math.max(
    1,
    Math.min(
      SUN_CASCADES_MAX,
      Math.floor(override ?? (authoredCascades ? sun.cascades! : row.cascades)),
    ),
  );
  const distanceM =
    distanceOverride !== null && distanceOverride > 0
      ? distanceOverride
      : authoredDistance
        ? sun.shadowDistanceM!
        : row.distanceM;
  // ⭐ ONE RESOLVER, THREE CALLERS (the other two are `worldLights.applyShadow` and
  // `resolveLightRig`'s `shadowMapBytes`). Before this, `mapSizeCap` had exactly ONE reader —
  // this line — and no test that made it bind differently at a different tier, which is this
  // repo's signature "compiled, passed, did nothing" shape.
  const near = shadowMapSizeFor(sun.shadowMapSize, row.mapSizeCap, resScale);
  const mapSizes = Array.from({ length: cascades }, (_, i) => (i === 0 ? near : near / 2));
  // R5: derived from the cascade reach and the ladder that have just been resolved, never authored
  // beside them — which is what keeps `shadowDistanceM` the one control over the frozen map's reach.
  const breaks = cascadeBreaksM(cascades, distanceM);
  const split = resolveSunShadowSplit(splitMode, cascades, distanceM, near, undefined, breaks);
  const bytes = sun.castShadow
    ? mapSizes.reduce((sum, s) => sum + s * s * SHADOW_MAP_BYTES_PER_TEXEL, 0) +
      // ⚠️ R5: THE STATIC MAP IS COUNTED, because it is resident every frame whether it re-rendered
      // or not. At tier 2 that is a second 2048² — 33.55 MB on top of 48.0 — and the `vramAccountant`
      // lesson is that an UNDER-stating budget instrument is the failure this repo has already had.
      (split.enabled ? split.staticMapSize * split.staticMapSize * SHADOW_MAP_BYTES_PER_TEXEL : 0)
    : 0;
  return {
    enabled: true,
    cascades,
    distanceM,
    // ONE ladder, computed once above and read by both the policy and the split — two calls would
    // be two answers to "where do the cascades hand over".
    breaksM: breaks,
    mapSizes,
    fade: row.fade,
    lightMarginM: CSM_LIGHT_MARGIN_M,
    /*
     * ⭐ B6: N CASCADES ARE N DEPTH PASSES, AND THE ARM AMORTISES THEM THE SAME WAY IT AMORTISES A
     * LOCAL LIGHT'S CUBE FACES — one rule (`amortiseShadowTraversals`), two peaks. `staticOnly`
     * cannot reach here (it returned `single` above), so the only arm that moves this number is
     * `everyNFrames`, and this is a per-frame AVERAGE exactly like every other traversal count in
     * the model. `localShadowBudget` therefore hands the local lights what a cheaper sun leaves,
     * which is the arithmetic that was always intended — it was only ever unable to move.
     */
    traversals: sun.castShadow ? amortiseShadowTraversals(cascades, update, updateN) : 0,
    bytes,
    authored: { cascades: authoredCascades, distance: authoredDistance },
    tierRow: row,
    resScale: Number.isFinite(resScale) && resScale > 0 ? resScale : 1,
    shadowUpdate: update,
    shadowUpdateN: updateN,
    split,
  };
}

/**
 * ── ⭐ THE HANDOVER FRACTION — WHERE THE BAKED SUN VISIBILITY TAKES OVER FROM THE CASCADES ──────
 *
 * `0.75`, and it is READ OUT OF r185 rather than chosen. `CSMShadowNode._setupFade`
 * (CSMShadowNode.js:388-447) computes `margin = 0.25 · closestEdge²` and, past the last cascade's
 * centre, `closestEdge` is that cascade's `.y`. `_getBreaks`'s split callbacks all push a final
 * break of exactly `1` and `_setLightBreaks` writes `_cascades[last] = vec2(prev, 1)`, so
 * `closestEdge = 1`, `margin = 0.25`, `dist = min(ld − csmX, 1 − ld)` and
 * `ret = 1 − (1 − S)·(dist/0.25)`.
 *
 * ⭐ **THAT MEANS THREE ALREADY FADES THE SUN'S SHADOW OUT TO FULLY LIT OVER THE LAST 25 % OF THE
 * SHADOW DISTANCE** — 67.5 m to 90 m at tier 2. The handover band is therefore not a new mechanism
 * and not a tuning knob: it is the ramp three already emits, given a destination other than 1.
 *
 * ⚠️ **THE NO-FADE TIERS GET THE SAME LINE, AND WHAT IT BUYS THEM IS SMALLER THAN THE DESIGN
 * CLAIMED — MEASURED, NOT ASSUMED.** `SUN_SHADOW_TIERS` has `fade: true` only at tier 2; tiers 0/1
 * are one cascade to 40 m where `_setupStandard` returns S right up to the cascade edge and 1
 * outside it. The design asserted that putting the band in the last 25 % REMOVES that hard step;
 * `tests/renderGiSunVisibility.test.ts` evaluated the composed expression and it does not. `w` is
 * ~1 at the edge on both sides, so the composed value goes `S·V` → `V`: the step's magnitude moves
 * from `1 − S` to **`V·(1 − S)`** — SCALED by V, strictly smaller wherever the probes see
 * occlusion, and exactly today's step in open sky where V = 1 (which is where a hard shadow edge is
 * least visible anyway). The other price is deliberate double-darkening inside `[30, 40]` m, where
 * the cascade and the probes shadow the same wall and the term approaches `S·V`; it is bounded by V
 * and the exact factor is pinned in that file.
 *
 * The exit, named and NOT taken: a SECOND band formula for the no-fade arm, placed past the cascade
 * in `ld > 1` (which keeps growing — `viewZToOrthographicDepth` does not saturate). It would remove
 * the double-darkening and trade this step for `|S − V|`, which is not obviously smaller, at the
 * cost of a second formula and a second pin for one line that currently serves both arms. It needs
 * the owner's eye on a real frame, not a second guess from here.
 *
 * ⚠️ It is a fraction of `distanceM` and NOT of `distanceM − camera.near`, which is what the shader
 * ramp actually uses (`viewZToOrthographicDepth(positionView.z, near, shadowFar)` is 0 at `near`).
 * At our 0.1 m near plane the two differ by 2.5 cm at tier 2 — this number is REPORTED state, the
 * shader is the fact, and pretending to a precision the report does not have would be worse than
 * naming the gap.
 */
export const SUN_HANDOVER_FRACTION = 0.75;

/**
 * The metres over which the sun's shadow hands over from the cascades to the baked per-probe term.
 * `[0.75 · distanceM, distanceM]`; `[0, 0]` when the policy has no cascades (`?csm=0`), where
 * nothing is composed at all.
 */
export function sunHandoverBandM(policy: SunShadowPolicy): [number, number] {
  if (!policy.enabled) return [0, 0];
  return [SUN_HANDOVER_FRACTION * policy.distanceM, policy.distanceM];
}

/** The sentence on the sun row while it is cascaded. Present only while the cause is (tier 3). */
export function sunCascadesNote(policy: SunShadowPolicy): string {
  return (
    `The sun's shadow is ${policy.cascades} cascade(s) (${policy.mapSizes.join('/')}) fitted to ` +
    `the camera out to ${policy.distanceM} m - ${policy.traversals} depth pass(es) per frame, ` +
    `${(policy.bytes / (1024 * 1024)).toFixed(1)} MB of maps. Shadow extent (m) sizes only the ` +
    'single map the BAKE photographs; the live frame uses Shadow distance (m).'
  );
}

/**
 * ── ⭐ THE RESOLVED SUN-SHADOW LINE — BECAUSE THE PANEL WAS DRAWING THE FILE, NOT THE RIG ────────
 *
 * The owner, twice, on the same screenshot: *"I don't know what shadow cam distance does, but it
 * doesn't seem to do anything. Furthermore, shadow extent - what is that meant to do? They both
 * seem to be doing the same thing."* He is right, and the system-shaped statement is that THERE
 * ARE TWO AUTHORITIES OVER THE SUN'S SHADOW AND THE PANEL READ NEITHER OF THEM. `WorldSunSchema`
 * holds four shadow numbers; `SUN_SHADOW_TIERS` holds three more; `sunShadowPolicy` is already the
 * ONE fold of the two — and Details drew the four raw FILE fields with no idea which of them the
 * fold used. Hence two dead fields at the top of the section and the two live ones rendering as a
 * literal `0` (`inspector.ts`'s number fallback for an `.optional()` key with no default).
 *
 * This is that fold, spelled. It invents no number: every term is read off the `SunShadowPolicy`
 * the frame is running (`csmRig.policy()`), and `authored` says which of the two an artist can
 * move came from the world file rather than from the tier.
 */
export function describeSunShadowPolicy(policy: SunShadowPolicy): string {
  if (!policy.enabled) {
    const head =
      `Single origin map ${policy.mapSizes.join('/') || '(no shadow)'} - ` +
      `${policy.traversals} depth pass(es), ${(policy.bytes / (1024 * 1024)).toFixed(1)} MB. `;
    // B6: two ways to reach the single map now, and a reader acts on WHICH. Saying "?csm=0" over a
    // world that authored a frozen sun would send an artist looking for a URL parameter nobody set.
    return policy.shadowUpdate === 'staticOnly'
      ? head +
          'FROZEN by Shadow update = staticOnly, which is why there are no cascades: a cascade box ' +
          'is fitted to the camera, so a frozen one is a picture of where you used to be. This box ' +
          'is world-anchored (+/- Shadow extent about the origin) and is rendered once.'
      : head +
          'The ?csm=0 control arm: the live map IS the one the bake photographs, so it is not ' +
          'capped by the tier and not scaled by the shadowRes render setting.';
  }
  const src = (authored: boolean): string => (authored ? 'file' : 'tier');
  /*
   * ⭐ THE SPLIT GETS ITS OWN LINE, BECAUSE THE ARTIST READING THIS IS THE PERSON WHO PAYS FOR IT.
   *
   * `bytes` grew by a whole map and `depth pass(es)` did not move, which without a sentence reads
   * as an accounting error. And the COST of the default is a picture — static shadows past the last
   * live break come from a frozen map at a coarser texel size, and a static object that moves keeps
   * a stale shadow until a refit — so it is named here in the metres and centimetres an artist
   * works in, beside the flag that turns it off. `describeSunShadowPolicy`'s existing arms already
   * carry exactly this kind of sentence for `staticOnly` and `?csm=0`.
   */
  const split = policy.split.enabled
    ? `\nstatic shadows ${policy.split.frozenFromM}-${policy.split.staticRadiusM} m come from ONE ` +
      `frozen map (${policy.split.staticMapSize} px, ` +
      `${((2 * policy.split.staticRadiusM * 100) / policy.split.staticMapSize).toFixed(1)} cm/texel) ` +
      `re-rendered only when you move ${policy.split.refitM} m - nearer than ` +
      `${policy.split.frozenFromM} m is unchanged. It is what makes the cascades cheap; ` +
      `?shadowSplit=off restores the old rig.`
    : '';
  return (
    `${policy.cascades} cascade(s) ${policy.mapSizes.join('/')} to ${policy.distanceM} m - ` +
    `${policy.traversals} depth pass(es), ${(policy.bytes / (1024 * 1024)).toFixed(1)} MB\n` +
    `cascades: ${src(policy.authored.cascades)} - distance: ${src(policy.authored.distance)}` +
    split
  );
}

/**
 * ── ⭐ ONE REACH LADDER, REPORTED, NEVER COUPLED ────────────────────────────────────────────────
 *
 * The single sentence the panel could not express: **the sun's shadow is exact out to
 * `shadowDistanceM`; from `0.75 x shadowDistanceM` it crossfades to the baked per-probe sun
 * visibility; that term only means anything INSIDE an authored GI volume, because outside the
 * indirection grid the fetch clamps to a border constant; fog is a third, independent authority.**
 *
 * Three ranges that all answer "how far does the sun's shadow reach" and never agreed to be
 * compared. On the COMMITTED `sponza-debug-01` fixture they read: probes 0-58.9 m (`world`,
 * `arcade`), cascades 0-90 m with the handover band 67.5-90 m, fog 60-150 m — i.e. **the whole
 * handover band lies OUTSIDE the probe field and hands to a clamped constant.** That is the tell,
 * and it is the Bake panel's air-percentage row in shape: a number an artist can act on, produced
 * by no coupling at all.
 *
 * ⚠️ THE NUMBERS ABOVE ARE POINT-TO-AABB DISTANCES FROM THE ORIGIN, NOT HALF-EXTENTS. This header
 * used to read "probes 25-33 m", which is the `arcade` volume's half-extents copied out of the
 * world file — the origin lies INSIDE that box on x and z and on its face in y, so the true near
 * distance is 0 m. The body's own comment records the correction and
 * `tests/renderLightRig.test.ts` pins it; the entry point must not keep restating the arithmetic
 * the body was written to kill, because the header is what gets quoted.
 *
 * ⛔ REFUSED, ON THE OWNER'S OWN RULING: no auto-fit of `shadowDistanceM` to the GI bounds and no
 * clamping of the band to the volume. He has already ruled that the probe field ending where the
 * probes are placed is fine and expected; what was missing was SEEING the relationship and being
 * TOLD when the cascades outrun coverage. That is this one warning string and nothing more.
 *
 * ⚠️ IT IS WORLD-SPACE AGAINST A CAMERA-DEPTH BAND, and the `hint` beside it says so. A volume's
 * half-extent is a fact about the AUTHORING; the handover band is a distance from wherever the
 * camera is. This is a statement about the level, never about a particular frame.
 */
export interface ShadowReachVolume {
  readonly id: string;
  readonly min: readonly number[];
  readonly max: readonly number[];
}

export interface ShadowReachInput {
  /**
   * ⚠️ `enabled` IS LOAD-BEARING AND ITS ABSENCE MEANS OFF. `world/schema.ts`'s `authoredGiVolumes`
   * is the ONE answer to "which volumes does this world compile", it gates on exactly this field,
   * and its header records what a second copy costs: `bake-world.ts` had one that read `volumes`
   * directly and reported *"authors 2 gi volume(s)"* over a page that had correctly synthesised
   * one. This is that same read, so it takes the same gate — `GiSchema.enabled` defaults to
   * `false`, so a parsed world always carries it, and a hand-written fixture that omits it is
   * asking about a world whose volumes are not compiled.
   *
   * ⛔ NOT AN IMPORT. This file imports NOTHING (`world/schema.ts` imports IT, and `bake-world.ts`
   * runs that chain unbundled under `node --experimental-strip-types`), so the gate is spelled
   * here and `tests/renderLightRig.test.ts` holds the two spellings to the same verdict.
   */
  readonly gi?:
    | { readonly enabled?: boolean; readonly volumes?: readonly ShadowReachVolume[] }
    | undefined;
  /**
   * `mode` matters as much as the range: in `height` mode `nearM`/`farM` are not read by the frame
   * at all (`fogModel.fogFields` HIDES them from the panel for that reason), so printing them in a
   * ladder an artist is asked to compare against would be the panel-lies defect this whole stage
   * exists to end — one surface over.
   */
  readonly fog?:
    | {
        readonly enabled?: boolean;
        readonly mode?: string;
        readonly nearM?: number;
        readonly farM?: number;
        readonly topM?: number;
        readonly density?: number;
      }
    | undefined;
}

export interface ShadowReach {
  /** Nearest face and farthest corner of the union of authored GI volumes, from the origin, m. */
  readonly probes: { readonly nearM: number; readonly farM: number; readonly names: string } | null;
  readonly cascades: { readonly nearM: number; readonly farM: number };
  readonly handover: readonly [number, number];
  /**
   * `null` ⇒ fog is off. `mode: 'height'` ⇒ `nearM`/`farM` are NOT what the frame runs and the line
   * says so instead of quoting them; the height terms are reported in their own units.
   */
  readonly fog:
    | { readonly mode: string; readonly nearM: number; readonly farM: number; readonly topM: number; readonly density: number }
    | null;
  /** Three lines, drawn as one readout. */
  readonly lines: readonly string[];
  /** Present ONLY while the handover band starts beyond the probe field. */
  readonly warning: string | null;
}

/**
 * The fog range a world that authors none is running — `scene.applyFog`'s reproduction of the
 * hard-coded `Fog(0x1a2836, 60, 150)`. Spelled here rather than imported because this file imports
 * NOTHING (`world/schema.ts` imports it, and `bake-world.ts` runs that chain unbundled).
 */
export const DEFAULT_FOG_RANGE_M: readonly [number, number] = [60, 150];

/**
 * `WorldFogSchema`'s height defaults, spelled here for the same reason `DEFAULT_FOG_RANGE_M` is —
 * this file imports NOTHING. `tests/renderLightRig.test.ts` holds them to the schema's own values,
 * so a retune there cannot leave this ladder quoting a number nobody renders.
 */
export const DEFAULT_FOG_HEIGHT: { readonly topM: number; readonly density: number } = {
  topM: 40,
  density: 0.00025,
};

/** The hint under the ladder — three ranges that are not measured from the same thing. */
export const SHADOW_REACH_HINT =
  'A statement about the LEVEL, not about a frame: the probe range is world-space from the origin ' +
  'and the cascade/fog ranges are distances from the camera. Nothing here is coupled — the sun ' +
  'does not fit itself to the GI volumes and never will.';

export function describeShadowReach(lighting: ShadowReachInput, policy: SunShadowPolicy): ShadowReach {
  /*
   * ⭐ THE SAME GATE `world/schema.ts`'s `authoredGiVolumes` APPLIES, AND FOR THE SAME REASON.
   *
   * `gi.enabled === false` means NONE of these volumes is compiled — the bake synthesises one from
   * the drawn bounds instead — so reading `gi.volumes` directly reported a probe range over a
   * lattice that does not exist, AND suppressed the ladder's one warning (`handover[0] > farM` is
   * false against a fabricated 58.9 m) in exactly the world that most needs it. That is the second
   * answer `authoredGiVolumes` was written to kill, one file over; its header records what the
   * first one cost ("authors 2 gi volume(s)" over a page that had compiled 1).
   */
  const giOn = lighting.gi?.enabled === true;
  const authored = lighting.gi?.volumes ?? [];
  const volumes = giOn ? authored : [];
  let probes: ShadowReach['probes'] = null;
  if (volumes.length > 0) {
    /*
     * Distance from the WORLD ORIGIN to each volume's nearest face and farthest corner, folded
     * over the union. Per axis: the near term is 0 while the origin's coordinate lies inside the
     * span (the standard point-to-AABB distance), and the far term is the larger |bound|.
     *
     * ⚠️ THE DESIGN'S WORKED EXAMPLE FOR THIS WAS WRONG AND THE ARITHMETIC IS RECORDED HERE RATHER
     * THAN INHERITED. It read Sponza's `arcade` (the WORKING file's single volume, min [-25,0,-30]
     * max [25,25,33]) as "nearest face 25 m, farthest xz corner ~41.4 m" — that is a HALF-EXTENT,
     * not a distance from the origin. The origin lies inside the box on x and z and ON its face in
     * y, so the true point-to-AABB distance is 0 m and the farthest corner is
     * sqrt(25² + 25² + 33²) = 48.4 m (the COMMITTED fixture authors two volumes and reads 58.9 m).
     * `tests/renderLightRig.test.ts` pins both against the owner's real file. The CONCLUSION the
     * design drew is unchanged and is the whole reason the ladder exists: tier 2's handover band
     * starts at 67.5 m, which is past 48.4 m, so it still hands to a clamped border constant.
     */
    let nearM = Infinity;
    let farM = 0;
    for (const v of volumes) {
      let near2 = 0;
      let far2 = 0;
      for (let a = 0; a < 3; a++) {
        const lo = Number(v.min[a] ?? 0);
        const hi = Number(v.max[a] ?? 0);
        const outside = lo > 0 ? lo : hi < 0 ? -hi : 0;
        near2 += outside * outside;
        const corner = Math.max(Math.abs(lo), Math.abs(hi));
        far2 += corner * corner;
      }
      nearM = Math.min(nearM, Math.sqrt(near2));
      farM = Math.max(farM, Math.sqrt(far2));
    }
    probes = {
      nearM: Math.round(nearM * 10) / 10,
      farM: Math.round(farM * 10) / 10,
      names: volumes.map((v) => v.id).join(', '),
    };
  }
  const handover = sunHandoverBandM(policy);
  const cascades = { nearM: 0, farM: policy.enabled ? policy.distanceM : 0 };
  /*
   * ⭐ ABSENT FOG MEANS **NO FOG**, AND THIS IS THE SECOND COPY OF THAT ANSWER BY NECESSITY.
   * `world/schema.resolveFogConfig` is the one authority (its header carries the ruling and the
   * reason: an absent block drawing `Fog(0x1a2836, 60, 150)` is the forced ground plane again).
   * This file may not import it — `world/schema.ts` imports THIS module, the hard constraint in
   * the header above — so the fold is re-spelled here and held to the original, value for value,
   * by `tests/renderFogModel.test.ts`. That test is the only thing stopping these two drifting,
   * which is exactly how the five readers came to agree on the wrong answer in the first place.
   */
  const fogOn = lighting.fog !== undefined && lighting.fog.enabled !== false;
  const fog = fogOn
    ? {
        mode: lighting.fog?.mode ?? 'linear',
        nearM: lighting.fog?.nearM ?? DEFAULT_FOG_RANGE_M[0],
        farM: lighting.fog?.farM ?? DEFAULT_FOG_RANGE_M[1],
        topM: lighting.fog?.topM ?? DEFAULT_FOG_HEIGHT.topM,
        density: lighting.fog?.density ?? DEFAULT_FOG_HEIGHT.density,
      }
    : null;
  const lines = [
    probes === null
      ? authored.length > 0
        ? // NOT "none authored" — the volumes exist in the file and are switched off, and telling
          // an artist they authored nothing while they are looking at their own volume list is the
          // confident-wrong answer this ladder exists to replace.
          `probes: ${authored.length} volume(s) authored but gi.enabled is false — none compiled ` +
          '(the bake synthesises one volume from the drawn bounds)'
        : 'probes: none authored (the bake synthesises one volume from the drawn bounds)'
      : `probes ${probes.nearM}-${probes.farM} m (${probes.names})`,
    policy.enabled
      ? `cascades 0-${cascades.farM} m, handover ${handover[0]}-${handover[1]} m`
      : 'cascades: off (?csm=0) - one origin map, no handover band',
    fog === null
      ? 'fog: off'
      : // ⚠️ MODE FIRST. `nearM`/`farM` are camera-depth metres that a HEIGHT fog does not read at
        // all — `fogModel.fogFields` hides them from the panel for exactly that reason — so quoting
        // them here as a peer of the cascade band would put a number the frame ignores into the one
        // readout an artist is being asked to compare ranges in.
        fog.mode === 'height'
        ? `fog: height - top ${fog.topM} m (world Y), density ${fog.density} 1/m^2 - no camera range`
        : `fog ${fog.nearM}-${fog.farM} m`,
  ];
  const warning =
    probes !== null && policy.enabled && handover[0] > probes.farM
      ? `The sun's shadow hands over to the baked probe visibility at ${handover[0]} m, past the ` +
        `farthest authored probe (${probes.farM} m). Outside the indirection grid that fetch ` +
        'clamps to a border constant, so the handover has nothing to hand to. Reported, not ' +
        'coupled: shorten Shadow distance (m), extend a GI volume, or leave it.'
      : null;
  return { probes, cascades, handover, fog, lines, warning };
}

/**
 * ── ⭐ THE TABLE. Every (type × mobility) pair, answered ONCE. ───────────────────────────────────
 *
 * A pair this pipeline cannot honour is REFUSED with its reason, never silently downgraded to a
 * neighbouring arm — a downgrade is a light that renders plausibly and is not the light the artist
 * asked for, which is this subsystem's entire failure history in one sentence.
 */
export function resolveLightSupport(type: LightType, mobility: LightMobility): LightSupport {
  if (type === 'skylight') {
    /*
     * A SKYLIGHT IS NOT A `lights[]` ENTRY ON THIS STACK, in any mobility, and the two refusals
     * differ because the two reasons differ.
     *
     * The supported skylight already ships and is authored elsewhere: `world.lighting.sky` puts a
     * real `SkyMesh` EMITTER in the scene (`src/render/sky.ts`), the probe bake photographs it,
     * and `resolveIndirect()` drops the constant fill to `AMBIENT_FILL_SKYLIT_RESIDUAL`. That IS
     * "a hemisphere light that knows about occlusion" — a static skylight — and it is measured
     * (indirect.ts's G4.2 residual table). The type stays in the vocabulary rather than being
     * omitted precisely so an artist who looks for "skylight" is TOLD where the real one is,
     * instead of finding nothing and concluding we have none.
     */
    if (mobility === 'static') {
      return {
        supported: false,
        presence: 'none',
        inBake: false,
        note:
          "A world's skylight is `lighting.sky`, not a `lights[]` entry: the sky dome is a real " +
          'emitter the probe bake photographs, which is what makes it occlusion-aware. Author it ' +
          'in the Sky block (sky.enabled) and re-bake — that path already ships and is measured. ' +
          "It is the 'sky' row of the resolved light rig (resolveLightRig), so the redirect lands " +
          'on something an artist can select rather than on a JSON path they have to go find.',
      };
    }
    return {
      supported: false,
      presence: 'none',
      inBake: false,
      note:
        'A DYNAMIC skylight needs a runtime sky IBL. scene.environment (PMREM) would be a THIRD ' +
        'implementation of the indirect term and is deliberately unused — src/render/indirect.ts ' +
        'is the law and src/render/sky.ts is the ruling. Use lighting.sky + a re-bake instead — ' +
        "the 'sky' row of the resolved light rig (resolveLightRig) is that block, selectable.",
    };
  }
  if (mobility === 'static') {
    return { supported: true, presence: 'bake', inBake: true, note: MOBILITY_NOTE.static };
  }
  return {
    supported: true,
    presence: 'runtime',
    inBake: mobility === 'stationary',
    note: MOBILITY_NOTE[mobility],
  };
}

/**
 * ── ⭐ PREVIEW LIGHTING FOR AN UNBAKED STATIC LIGHT — ONE MORE REASON TO BE `runtime` ───────────
 *
 * The owner's sentence: *"static lights who haven't been baked yet (e.g. settings changed or newly
 * added) [should] render preview lighting into the scene, so that artists can dial the light in.
 * But obviously, that light doesn't emit dynamic light once baked, and never shipped if the bake
 * is stale."*
 *
 * This is NOT a new light kind and NOT a new mobility. Mobility already means exactly one thing —
 * WHERE A LIGHT EXISTS (`LightPresence`) — and "its energy is not in the bound atlas yet" is a
 * reason for a `static` light to exist RIGHT NOW. So the whole feature is this identity function
 * with one arm, threaded through the ONE fold that prices rows (`resolveLightRig`) and the ONE rig
 * that builds them (`worldLights.reconcile`), which is what stops the row's promise and the
 * frame's behaviour drifting apart — the same shape `effectiveShadowUpdate` uses for `budgeted`.
 *
 * ⚠️ IT WRITES NOTHING AND AUTHORS NOTHING. `previewing` is DERIVED, per session, by the editor
 * (`world.lighting.indirect === 'baked' && (bakeState !== 'baked' || worldEditedSinceLoad())`);
 * there is no schema field, so `world.json` is byte-identical whether preview ran or not and
 * `worldHash` cannot move. That is fence 1 of four, and it is the strongest: THERE IS NOTHING TO
 * SHIP. (Fence 2 is the `import.meta.env.DEV` fold on `GameScene.setLightPreview` plus its
 * `checkBundle` absence leg; fence 3 is `WorldLightsInfo.previewParented`, counted off
 * `light.parent`; fence 4 is the bake bracket, which forces this false around every capture loop
 * so an atlas is never photographed with preview deciding presence.)
 *
 * `inBake` stays TRUE on the preview arm: a static light belongs in the atlas, and the only reason
 * it is in the graph at all is that the atlas does not have it yet. Nothing reads it in that state
 * today — the bake bracket turns preview off before it asks — and the honest value is the one that
 * stays right if that ever changes.
 */
export function previewPresence(support: LightSupport, previewing: boolean): LightSupport {
  if (!previewing || support.presence !== 'bake') return support;
  return { ...support, presence: 'runtime', inBake: true };
}

/**
 * The third-tier warning a previewing light carries, present only while the cause is.
 *
 * ⚠️ IT IS NOT COSMETIC AND IT IS NOT OPTIONAL. `MOBILITY_NOTE.static` says it plainly: we have no
 * lightmaps, so after a bake ONLY this light's one-bounce INDIRECT survives — its DIRECT term is
 * gone. A preview that parents a real light therefore shows a SHARPER, BRIGHTER room than the bake
 * will ever produce. UE has exactly this gap and accepts it, so the feature is right; shipping it
 * unlabelled would be teaching artists to dial in a look they cannot get.
 */
/**
 * ── THE PREDICATE, AND IT COMPUTES NO NEW VERDICT ───────────────────────────────────────────────
 *
 * "Is this world's static lighting currently NOT in the frame?" — the AND-NOT of two clocks this
 * codebase already keeps ON PURPOSE (`worldWrite.ts` says so in those words), and nothing more:
 *
 *   · `bakeState` — the LOAD-time verdict about the file on disk (`render/gi/bakeState.ts`,
 *     derived from `checkProvenance`). Anything but `'baked'` — stale, unbaked, refused, or no
 *     volume bound at all — means this world's static lights are not in the bound atlas.
 *   · `editedSinceLoad` — the editor's dirty flag. A bound-and-clean atlas plus an edit since load
 *     means the file describes a world that is no longer the one on screen.
 *
 * ⚠️ THE `indirect === 'baked'` LEG IS LOAD-BEARING, not defensive. In a `fill` world a `static`
 * light is invisible FOREVER and no bake changes that (`STATIC_UNBAKED_NOTE` is already the
 * answer there). Previewing one would show an artist light with no path to truth.
 *
 * ⚠️ WORLD-LEVEL, NEVER PER-LIGHT. "This light was added this session" is derivable from the
 * editor's clean snapshot, and deriving it would be a THIRD staleness authority beside
 * `checkProvenance` and the dirty flag. The atlas is whole-world; so is this.
 *
 * It lives HERE, beside `previewPresence`, because the two halves of the feature — when to preview
 * and what previewing MEANS — drift the moment they live in different files. Pure, no imports,
 * strings in: `bakeState` is passed as a string so this module stays free of `render/gi`'s chain
 * (`bake-world.ts` runs it unbundled under node's type stripper).
 */
export function wantsStaticPreview(
  indirect: string,
  bakeState: string | null | undefined,
  editedSinceLoad: boolean,
): boolean {
  if (indirect !== 'baked') return false;
  return bakeState !== 'baked' || editedSinceLoad;
}

export const STATIC_PREVIEW_NOTE =
  'PREVIEW ONLY — this light is NOT in the bound atlas yet (new, edited, or the bake is stale), ' +
  'so it is being lit DYNAMICALLY at full runtime cost to let you dial it in. It disappears the ' +
  'moment a fresh atlas binds, and it never ships. ⚠️ The bake does NOT keep its direct light (we ' +
  'have no lightmaps) — only its one-bounce indirect survives, so the baked result is SOFTER and ' +
  'DIMMER than this. Re-bake to see the real thing.';

/**
 * ── THE BUDGET THE ARTIST SEES BEFORE THE HITCH, NOT AFTER ──────────────────────────────────────
 *
 * `LightsNode.customCacheKey()` hashes every light's `id` and `castShadow` into every render
 * object's DYNAMIC cache key, and a changed dynamic key makes `RenderObjects.get` dispose and
 * rebuild the object (`renderers/common/RenderObjects.js`). So **adding or deleting one light
 * regenerates the WGSL for every visible material** — on Sponza, ~60 materials, a multi-hundred-
 * millisecond main-thread hitch, per add and per delete. That is a property of three's design, not
 * of ours, and the only lever we have is the light COUNT.
 *
 * Hence a budget, shown live, per tier — and `static` lights DO NOT COUNT, because they are never
 * added to the scene. That is the whole point of the static arm and the panel says so on the same
 * line. Shadow casters are budgeted separately: each is a shadow map and a depth pass (~3 ms
 * measured, `src/render/indirect.ts`), which is why `castShadow` defaults false.
 *
 * The numbers are the draw-call budget's siblings (CLAUDE.md hard rule 2: ≤100 tier 0 / ≤300
 * tier 2) and, like those, they are a design ceiling to measure against rather than an enforced
 * cap: `buildWorldLights` REPORTS the overrun, it never drops a light the artist authored. A
 * silent cap would be the "silently downgraded" failure the refusal table exists to prevent.
 */
export function lightBudget(tier: number): { runtime: number; shadow: number } {
  if (tier <= 0) return { runtime: 8, shadow: 2 };
  if (tier === 1) return { runtime: 16, shadow: 4 };
  return { runtime: 24, shadow: 6 };
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ── ⭐ L1 · ONE LIST, ONE VERDICT — THE SUN AND THE SKY BECOME ROWS WITHOUT MOVING A BYTE ───────
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * THE SYSTEM STATEMENT, before any of the code below. A world's lighting is TWO TABLES today:
 *
 *   `lighting.{sun,sky,ambient}`  — one bespoke row each. No mobility, no verdict, no sprite, no
 *                                   outliner row, no inspector, no delete.
 *   `lighting.lights[]`           — an arbitrary list WITH a (type × mobility) verdict table, a
 *                                   reconciler, gizmos, a schema-driven inspector and a budget.
 *
 * Everything the owner asked for — "different light types, an add button, sprites of the lights,
 * convert all current lights to our new light types" — is a property of the SECOND table that the
 * FIRST does not have. So the work is not "add light types" (they exist, `LIGHT_TYPES` above). It
 * is collapsing two tables into ONE LIST. And the whole cost of doing that badly is `worldHash`.
 *
 * ── ⚠️ WHY WE RESOLVE AND DO NOT MIGRATE — FOUR REASONS, EACH ONE SUFFICIENT ────────────────────
 *
 * Taken literally, "convert the current lights" means moving `lighting.sun` into `lighting.lights`.
 * That is not a refactor, it is a RE-BAKE OF EVERY BAKED WORLD ON EVERY DISK:
 *
 *  1. `probeFile.checkProvenance` fingerprints a SINGLE sun (`ProbeFileLighting.sunDir`/
 *     `sunIntensity`/`sunColor`) precisely because the dev overrides `?sun=`/`?sunAz=`/`?sunEl=`
 *     are NOT in `worldHash`. With the sun as light #0 of N, "which of these is the sun" has no
 *     answer in the data, and inventing one is a `PROBE_FILE_FORMAT` bump — i.e. every atlas on
 *     every disk refused, including the owner's 46,149-capture Sponza bake.
 *  2. `createSky(cfg, sunPos)` drives the dome's `sunPosition` uniform from the SAME vector as the
 *     light, which is what makes them unable to disagree. Two directional lights make "which one
 *     aims the sky?" a new question with no answer in the data.
 *  3. `WorldSunSchema`'s defaults ARE the shipped gym rig, pinned byte-for-byte by
 *     `tests/renderSky.test.ts` including the polar form of `(12, 18, 8)`.
 *  4. `WorldSunSchema`/`WorldSkySchema`/`WorldAmbientSchema` are `prefault({})`, so EVERY field
 *     materialises on parse. Touching any of them moves `worldHash` for all six committed worlds.
 *     `lights[]` is `.optional()` and verified ABSENT from all six, which is why it may grow.
 *
 * ⇒ **RESOLVE, DO NOT MIGRATE.** `resolveLightRig()` below folds the two tables into one list of
 * `ResolvedLight` rows. Nothing on disk moves, no atlas goes stale, no re-bake — and the sun and
 * the sky get a verdict, a field list, a cost and a `writeTarget` an inspector DISPATCHES on
 * rather than chooses. `tests/renderLightRig.test.ts` asserts `worldHash` byte-identical across
 * this change for every committed world; that is the fence the whole design hangs on.
 *
 * WHAT IT MAKES HARDER: one list with two write targets — a sun row writes `lighting.sun`, an
 * authored row writes `lighting.lights[i]`. That is the price of not moving the hash, and the
 * fence is that each row CARRIES its own target, so no consumer ever branches on `role` to decide
 * where to write.
 *
 * HOW WE WOULD KNOW IT STOPPED WORKING: `__fwg.state().lights.rows` beside `parented`/`runtime`,
 * plus `shadowTraversals` and `costUnits` — see `worldLights.ts`.
 *
 * ── AND THE ONE THING THAT MUST NEVER BECOME A ROW ──────────────────────────────────────────────
 *
 * `lighting.ambient` — the constant fill. `src/render/indirect.ts` is the law: `applyIndirect()`
 * is the ONLY writer of `hemi.intensity`, and a row implies an editable intensity sitting next to
 * the lights. Editing it *is* the double-count that file exists to prevent. The fill is a READOUT
 * sourced from `scene.indirect()`, never a row, and `resolveLightRig` deliberately returns no row
 * for it. A `skylight` in `lights[]` is refused for the same reason (the table above).
 */

/** Which table a resolved row came from — and therefore which fence it lives behind. */
export const LIGHT_ROLES = ['sun', 'sky', 'authored'] as const;
export type LightRole = (typeof LIGHT_ROLES)[number];

/**
 * ── THE FIELD KEY SETS, DECLARED HERE RATHER THAN IMPORTED ─────────────────────────────────────
 *
 * `src/world/schema.ts` imports THIS file (for `LIGHT_TYPES`/`LIGHT_MOBILITIES`), so this file
 * cannot import it back. The key lists below are therefore a second copy — and a second copy of a
 * rule is exactly what this codebase names as its recurring failure. The fence that makes it safe
 * is a TEST, not discipline: `tests/renderLightRig.test.ts` asserts each list is EXACTLY
 * `Object.keys(Schema.shape)`, so a field added to the schema and not to the list fails
 * `npm run check` in ~12 s rather than silently disappearing from an inspector.
 *
 * (The alternative — one shared constants module — would have to be imported by `schema.ts` AND by
 * this file, and `world/schema.ts` is reached by `tools/bundler/bake-world.ts`, which runs
 * UNBUNDLED under `node --experimental-strip-types`. Every hop on that chain is a hop that must
 * survive node's type stripper, so the chain is kept as short as it already is.)
 */
export const WORLD_LIGHT_FIELDS = [
  'id',
  'type',
  'mobility',
  'pos',
  'azimuthDeg',
  'elevationDeg',
  'distanceM',
  'color',
  'intensity',
  'radiusM',
  'coneDeg',
  'penumbra',
  'castShadow',
  'shadowMapSize',
  // L2. `decay` was riding three's default 2 unset and invisible; the other two are the shadow
  // update policy. All three are DEFAULTED on `WorldLightSchema`, which costs no committed world a
  // hash because `lights` is `.optional()` and none of the six authors one.
  'decay',
  'shadowUpdate',
  'shadowUpdateN',
] as const;

/** `WorldSunSchema`'s keys. Every one is meaningful — it is a purpose-built single-sun block. */
export const WORLD_SUN_FIELDS = [
  'azimuthDeg',
  'elevationDeg',
  'distanceM',
  'color',
  'intensity',
  'castShadow',
  'shadowExtentM',
  'shadowMapSize',
  // B3. Both `.optional()` with NO default, because `WorldSunSchema` is `prefault({})` and hashed:
  // an absent key stays absent from `canonicalJson`, so no committed `worldHash` moves. 0 = "the
  // tier decides" (`sunShadowPolicy`), which is what the inspector's number widget writes.
  'cascades',
  'shadowDistanceM',
  // B6. `.optional()` with NO default for exactly the reason the two above are — see them, and see
  // `WorldSunSchema` for the `canonicalJson` argument that makes an absent key hash-free.
  'shadowUpdate',
  'shadowUpdateN',
] as const;

/** `WorldSkySchema`'s keys. A dome is a MESH, so these are mesh parameters, not light parameters. */
export const WORLD_SKY_FIELDS = [
  'enabled',
  'scaleM',
  'turbidity',
  'rayleigh',
  'mieCoefficient',
  'mieDirectionalG',
  'cloudCoverage',
] as const;

/** Which fields are meaningful for a row, and what to call the ones whose name is ambiguous. */
export interface LightFieldPlan {
  /** Fields the inspector renders, in schema order. */
  shown: readonly string[];
  /**
   * Fields that exist in the schema and mean NOTHING for this type. HIDDEN, not warned: a hidden
   * field asks no question, while a warned field asks the artist to ignore something thirty times
   * a level. `shown ∪ hidden` is the whole schema key set — asserted, so nothing is silently lost.
   */
  hidden: readonly string[];
  /** Per-field label overrides. Only where the schema's key name is ambiguous or misleading. */
  labels: Readonly<Record<string, string>>;
}

/**
 * ── ⚠️ `radiusM` IS TWO FIELDS WEARING ONE NAME, AND THIS IS WHERE THAT IS SAID ─────────────────
 *
 * `WorldLightSchema.radiusM` means `light.distance` (the attenuation cutoff) for point and spot,
 * and the ORTHOGRAPHIC HALF-EXTENT of the shadow camera for directional — `worldLights.update()`
 * writes it into `shadow.camera.{left,right,top,bottom}`. `WorldSunSchema` calls that same
 * quantity `shadowExtentM`. So one lighting block carries one field with two meanings AND two
 * names for one meaning.
 *
 * The correct fix is ADDITIVE (`attenuationRadiusM` + `shadowExtentM` on `WorldLightSchema`, with
 * `radiusM` kept as the fallback read at parse) and it belongs to stage L2, because it is a schema
 * change and this stage's whole claim is that no world file moves. Until then the LABEL is the
 * fix, and it is not cosmetic: it is the difference between an artist tuning a falloff and an
 * artist silently resizing a shadow frustum.
 *
 * ── AND WHY "attenuation radius" IS MEANINGLESS ON A DIRECTIONAL LIGHT, precisely ───────────────
 *
 * Not "N/A by convention" — UNREPRESENTABLE. `DirectionalLightNode.setupDirect()` (three r185) is
 * three lines and contains no `getDistanceAttenuation` call at all; there is no distance term in
 * the shader to attenuate. `distanceM` survives on a directional row for the one thing it does do:
 * place the shadow camera.
 */
const LIGHT_FIELD_PLANS: Record<LightType, LightFieldPlan> = {
  directional: {
    shown: [
      'id',
      'type',
      'mobility',
      'azimuthDeg',
      'elevationDeg',
      'distanceM',
      'color',
      'intensity',
      'radiusM',
      'castShadow',
      'shadowMapSize',
      'shadowUpdate',
      'shadowUpdateN',
    ],
    // `decay` is a DISTANCE falloff exponent and a directional light has no distance term at all
    // (`DirectionalLightNode.setupDirect` — see `lightDistanceM`), so it is not merely unused here,
    // it is unrepresentable. Hidden, exactly like `radiusM`'s attenuation meaning.
    hidden: ['pos', 'coneDeg', 'penumbra', 'decay'],
    labels: {
      // NOT an attenuation radius — there is no distance term in a directional light's shader.
      radiusM: 'Shadow extent (m)',
      distanceM: 'Shadow cam distance (m)',
      azimuthDeg: 'From azimuth (deg)',
      elevationDeg: 'From elevation (deg)',
    },
  },
  point: {
    shown: [
      'id',
      'type',
      'mobility',
      'pos',
      'color',
      'intensity',
      'radiusM',
      'decay',
      'castShadow',
      'shadowMapSize',
      'shadowUpdate',
      'shadowUpdateN',
    ],
    // A point light has no direction, so the aim pair is meaningless; `distanceM` places a
    // directional light's shadow camera and nothing else.
    hidden: ['azimuthDeg', 'elevationDeg', 'distanceM', 'coneDeg', 'penumbra'],
    labels: {
      radiusM: 'Attenuation radius (m)',
      decay: 'Falloff exponent (2 = physical)',
      shadowUpdate: 'Shadow update',
      shadowUpdateN: 'Shadow every N frames',
    },
  },
  spot: {
    shown: [
      'id',
      'type',
      'mobility',
      'pos',
      'azimuthDeg',
      'elevationDeg',
      'color',
      'intensity',
      'radiusM',
      'coneDeg',
      'penumbra',
      'decay',
      'castShadow',
      'shadowMapSize',
      'shadowUpdate',
      'shadowUpdateN',
    ],
    hidden: ['distanceM'],
    labels: {
      radiusM: 'Attenuation radius (m)',
      decay: 'Falloff exponent (2 = physical)',
      shadowUpdate: 'Shadow update',
      shadowUpdateN: 'Shadow every N frames',
      coneDeg: 'Cone half-angle (deg)',
      // The convention trap the schema documents at length: a spot at elevation +45 shines DOWN.
      azimuthDeg: 'From azimuth (deg)',
      elevationDeg: 'From elevation (deg)',
    },
  },
  skylight: {
    // Every skylight row in `lights[]` is REFUSED (the table above), so nothing but the identity
    // triple is honoured — and `type` has to stay so the artist can change it back.
    shown: ['id', 'type', 'mobility'],
    hidden: [
      'pos',
      'azimuthDeg',
      'elevationDeg',
      'distanceM',
      'color',
      'intensity',
      'radiusM',
      'coneDeg',
      'penumbra',
      'castShadow',
      'shadowMapSize',
      'decay',
      'shadowUpdate',
      'shadowUpdateN',
    ],
    labels: {},
  },
};

const SUN_FIELD_PLAN: LightFieldPlan = {
  shown: WORLD_SUN_FIELDS,
  hidden: [],
  labels: {
    azimuthDeg: 'From azimuth (deg)',
    elevationDeg: 'From elevation (deg)',
    /*
     * ⭐ RENAMED BECAUSE THE OLD NAME WAS THE DEFECT (the owner: *"I don't know what shadow cam
     * distance does, but it doesn't seem to do anything"*). It does not change a directional
     * light's DIRECTION (`sunAngles.ts:28` says so plainly) and it does not change the LIVE
     * shadow: `csmRig` replaces the stock camera every frame, so the far plane this positions is
     * never the one that renders. What it still does is pose the light object — which places the
     * BAKE's stock shadow camera and the editor's sun sprite. Hence the parenthetical, and hence
     * the field's move to the collapsed `Bake & icon` section in `detailsModel.SUN_SECTIONS`.
     */
    distanceM: 'Sun icon distance (m - pose only)',
    // Under cascades this sizes ONLY the bake's single map (`csmRig` detaches for a bake); the
    // live frame's reach is `shadowDistanceM`. `sunCascadesNote` says so on the row. Since the
    // sun-visibility work the bake FITS its own box to the captures, so this is a fallback and a
    // comparison — his own bake header records `authoredExtentM: 30` beside a larger fitted one.
    shadowExtentM: 'Shadow extent (m, bake map fallback)',
    // ⚠️ BOTH OF THESE ARE RE-KINDED AND RE-LABELLED BY `detailsModel.sunPlan` WHENEVER A SCENE IS
    // RUNNING, with the tier's own answer spelled into the label ('Tier decides (3)'). These are
    // the headless fallbacks; they must never claim a tier number, because with no scene there is
    // no tier to claim one from.
    cascades: 'Shadow cascades (0 = tier decides)',
    shadowDistanceM: 'Shadow distance (m - 0 = tier decides)',
    // B6. Same words as a local light's row (`LIGHT_FIELD_PLANS.point/spot`), because it is the
    // same lever with the same three arms — a second vocabulary for one enum is how an artist
    // learns that the sun is "different" and stops reaching for it.
    shadowUpdate: 'Shadow update',
    shadowUpdateN: 'Shadow every N frames (0 = 4)',
  },
};

const SKY_FIELD_PLAN: LightFieldPlan = {
  shown: WORLD_SKY_FIELDS,
  hidden: [],
  labels: { enabled: 'Sky dome on', scaleM: 'Dome scale (m)' },
};

/**
 * The field plan for a row. `role` decides the SCHEMA; `type` only matters for an authored row.
 * Pure table lookup — `tests/renderLightRig.test.ts` holds every cell against the real zod shapes.
 */
export function lightFields(role: LightRole, type: LightType): LightFieldPlan {
  if (role === 'sun') return SUN_FIELD_PLAN;
  if (role === 'sky') return SKY_FIELD_PLAN;
  return LIGHT_FIELD_PLANS[type];
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ── ⭐ THE COST MODEL — BECAUSE TWO LIGHTS THAT BOTH COUNT 1 DIFFER BY 12.8× ────────────────────
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `lightBudget()` above returns ONE `runtime` number and counts every light as 1. Measured, that
 * is wrong by more than an order of magnitude:
 *
 *   light                                    unshadowed        shadow adds
 *   spot/directional, stock lighting, t0     3.00 ms Sponza    +2.96 ms, +33 draws, +111,803 tris
 *   point, stock lighting, t2                3.12 ms Sponza    ×6 GEOMETRY (+22,640 tris/light)
 *   point, clustered + a real radius, t2     0.243 ms          n/a (a shadowed point leaves it)
 *
 * So the unit is COST, not count. `lightCostUnits` is normalised to "one stock unshadowed light on
 * real geometry" ≈ 3 ms, and the numbers below are that table divided by ~3.0 — arithmetic over
 * AUTHORED data, unit-testable with no GPU, which is the only kind of budget this stage may ship.
 *
 * ⚠️ TWO COSTS THAT DO NOT MIX, and budgeting them together is the mistake:
 *   - an unshadowed light is PER FRAGMENT and scales with fill; dynamic resolution moves it.
 *   - a shadow caster is PER SCENE TRAVERSAL and scales with CONTENT; dynamic resolution CANNOT
 *     touch it. Hence `shadowTraversals` is reported as its own number and never folded into one.
 */

/** A shadowed POINT light is six full scene renders per frame — `PointShadowNode.renderShadow`. */
export const POINT_SHADOW_FACES = 6;

/**
 * Per-frame cost of one resolved row, in "stock unshadowed lights on real geometry" (≈3 ms each).
 *
 * A row that is not `runtime` costs ZERO, and that is the whole point of the static arm: it is not
 * in the graph, so it is not in `_lights`, so it is not in `LightsNode.customCacheKey()` and not
 * in the shader's loop. Zero ms, zero light slot, zero pipeline entry.
 */
export function lightCostUnits(input: {
  type: LightType;
  castShadow: boolean;
  presence: LightPresence;
  /**
   * L2. `undefined` reads as `'everyFrame'`, which is what every light did before the field
   * existed — so an unmigrated caller prices the same number it always did rather than a cheaper
   * one it did not ask for.
   */
  shadowUpdate?: ShadowUpdate;
  shadowUpdateN?: number;
  /**
   * `ClusteredLighting` is REJECTED as a default (D10) — this arm exists so the number can be
   * asked for when a world ever meets its trigger (≥16 unshadowed points, radii under a third of
   * the level diagonal, tier 2 only). Clustering never applies to a shadowed light at all.
   */
  clustered?: boolean;
}): number {
  if (input.presence !== 'runtime') return 0;
  const clustered = input.clustered === true && !input.castShadow && input.type === 'point';
  // 0.243 / 3.12 ≈ 0.078 — the measured clustered-vs-stock ratio on Sponza, rounded to 0.08.
  const base = clustered ? 0.08 : 1;
  if (!input.castShadow) return base;
  // The shadow term is exactly `shadowTraversals` in the same units: one traversal ≈ one stock
  // unshadowed light ≈ 3 ms. Deriving it from that one function rather than restating
  // `type === 'point' ? 6 : 1` here is what keeps the two numbers from disagreeing about a
  // `staticOnly` caster — the arm where they used to.
  return (
    base + shadowTraversals(input.type, input.castShadow, input.presence, input.shadowUpdate, input.shadowUpdateN)
  );
}

/**
 * Depth passes this row costs EVERY FRAME. The number that actually predicts the 2.96 ms/caster
 * measured on Sponza, and the reason a shadowed point light is not "one more shadow": it is SIX.
 *
 * A `static` (bake-presence) light contributes 0 here even when `castShadow` is true, and that is
 * not a fudge — it has no runtime shadow because it has no runtime presence. Its shadow map is
 * rendered ONCE PER BAKE (`probeGrid.freezeSceneForBake` sets `autoUpdate = false` and
 * `needsUpdate = true` before the capture loop), which is a build cost, not a frame cost.
 */
export function shadowTraversals(
  type: LightType,
  castShadow: boolean,
  presence: LightPresence,
  /** L2. `undefined` ⇒ `'everyFrame'` — the pre-L2 behaviour, so an unmigrated caller is unmoved. */
  shadowUpdate?: ShadowUpdate,
  shadowUpdateN?: number,
): number {
  if (presence !== 'runtime' || !castShadow) return 0;
  const faces = type === 'point' ? POINT_SHADOW_FACES : 1;
  // `'budgeted'` reaching here unresolved means the caller has no selection in hand, and the
  // honest number without one is the PEAK: `resolveLightRig` passes the EFFECTIVE arm
  // (`effectiveShadowUpdate`) when it knows the slots, which is where a frozen one prices 0.
  if (shadowUpdate === undefined || shadowUpdate === 'budgeted') return faces;
  return amortiseShadowTraversals(faces, shadowUpdate, shadowUpdateN);
}

/**
 * ── ⚠️ THE AMORTISATION RULE, ONCE — AND THE SPIKE IS A NOTE RATHER THAN A SECOND NUMBER ────────
 *
 * `everyNFrames` re-renders the WHOLE map on its refresh frame, so the worst frame still costs
 * `peak` traversals; only the average is `peak / N`. Publishing both a peak and a mean would put
 * two numbers under one budget and leave a reader to work out which one the ceiling is for. The
 * budget is a per-frame AVERAGE against an average frame time, so the average is the honest one to
 * report, and `SHADOW_EVERY_N_NOTE` says the spike out loud on the row that chose it.
 *
 * ⭐ B6: EXTRACTED SO THE SUN USES THE SAME ARITHMETIC. A local light's peak is its cube faces
 * (1, or 6 for a point); the CASCADED sun's peak is its cascade COUNT — N cascades are N depth
 * passes (`sunShadowPolicy.traversals`). Two different peaks, one rule about what an arm does to a
 * peak; a second copy of that rule beside the sun is how the frame-cost line and the frame come to
 * disagree about the one lever in this design that removes milliseconds.
 */
export function amortiseShadowTraversals(
  peak: number,
  update: AppliedShadowUpdate,
  shadowUpdateN?: number,
): number {
  if (update === 'staticOnly') return 0;
  if (update === 'everyNFrames') return peak / normalizeShadowUpdateN(shadowUpdateN);
  return peak;
}

/**
 * The N an `everyNFrames` map actually refreshes on, from whatever the world authored.
 *
 * `0`/absent ⇒ `DEFAULT_SHADOW_UPDATE_N`, which is what `WorldSunSchema.shadowUpdateN` means by
 * `0` (it is `.optional()` with no default — see there for why it may not have one — and the
 * inspector's select has no "absent" to offer, exactly as with `cascades`). Anything below 2 is
 * clamped to 2, because N = 1 is `everyFrame` spelled a second way and two spellings of one
 * behaviour is how a budget line and a renderer come to disagree.
 *
 * ONE normaliser, four readers: `amortiseShadowTraversals` (the priced row),
 * `worldLights.tickShadows` (the local frame), `csmRig` (the sun's frame) and the editor's label.
 */
export function normalizeShadowUpdateN(shadowUpdateN?: number): number {
  const n = shadowUpdateN ?? DEFAULT_SHADOW_UPDATE_N;
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_SHADOW_UPDATE_N;
  return Math.max(2, Math.floor(n));
}

/**
 * ── ⭐ THE `everyNFrames` SCHEDULE, AS ONE PURE PREDICATE ────────────────────────────────────────
 *
 * True on the frames the map at `phase` must re-render. `phase` is a PER-MAP OFFSET and without it
 * the LOD is a stutter rather than a saving: four `everyNFrames: 4` maps refreshing together is
 * 4 × 2.96 ms on one frame in four and nothing on the other three — the same total, delivered as a
 * hitch. The local rig phases by a light's index in the rig (the world's authoring order, stable
 * across reloads); the sun phases by CASCADE index, so its three passes land on three frames.
 */
export function shadowRefreshDue(frameIndex: number, phase: number, n: number): boolean {
  return (frameIndex + phase) % n === 0;
}

/**
 * The N an `everyNFrames` light uses when the world does not say. 4 is where the measured
 * amortisation (2.96 → 0.74 ms) was taken and it is two frames of lag at 60 Hz — enough to matter
 * only for a caster moving fast enough that its shadow is already motion-blurred by the display.
 */
export const DEFAULT_SHADOW_UPDATE_N = 4;

/** A per-tier ceiling for the cost model. REPORTED, never enforced — same rule as `lightBudget`. */
export interface LightCostBudget {
  /** Total `lightCostUnits` this tier can afford, ≈ ×3 ms of GPU delta on real geometry. */
  costUnits: number;
  /** Total `shadowTraversals` this tier can afford. A spot = 1, a directional = 1, a point = 6. */
  shadowTraversals: number;
  /**
   * ⚠️ TRUE means these numbers are a STRUCTURAL GUESS, not a measurement, and any surface
   * rendering them owes the caveat. NO TABLET HAS EVER BEEN MEASURED — stated six times in the
   * rendering roadmap and still true. Carrying it as DATA is what stops the guess being quoted as
   * a fact by the third surface that reads it.
   */
  provisional: boolean;
  /** Why this ceiling is what it is — one sentence, for the surface that shows the number. */
  basis: string;
}

/**
 * ⚠️ THE OPENING SENTENCE THIS TABLE IS DERIVED FROM: at 1.44 Mpix the whole remaining GPU budget
 * is ~0.72 ms (7.20 ms/Mpix budget, 6.68 already spent by the indirect term — ~82 % of it the
 * DISABLED Chebyshev depth term whose 8 loads run regardless). **We are over budget with zero
 * authored lights.** Every number below is therefore a DELTA against a zero-light baseline that is
 * itself already 18.4-21.6 ms on Sponza, and none of it is honest until the fixed-band scatter
 * (`probeBrick.ts`, "~3.9 of the ~6.4 is the SCATTER") is addressed. That is a GI stage, not a
 * lighting one, but it gates every number here.
 */
export function lightCostBudget(tier: number): LightCostBudget {
  if (tier <= 0) {
    return {
      costUnits: 2,
      shadowTraversals: 1,
      provisional: false,
      basis:
        'WebGL2 fallback: the standing ruling is sun-and-shadow only, and ClusteredLighting ' +
        'silently does not light here. A STATED CONTRACT, not a discovered blank.',
    };
  }
  if (tier === 1) {
    return {
      costUnits: 8,
      shadowTraversals: 1,
      provisional: true,
      basis:
        'PROVISIONAL - no tablet has ever been measured. Structural argument only: fill rate is ' +
        'the known mobile wall (DPR2 16 fps vs DPR1 48 fps, measured 2026-08-27) and every ' +
        'unshadowed light is per-fragment. Treat as a guess until a device reports.',
    };
  }
  return {
    costUnits: 24,
    shadowTraversals: 4,
    provisional: false,
    basis:
      'Desktop WebGPU, measured on Sponza: ~3 ms per stock unshadowed light and ~2.96 ms per ' +
      'shadow traversal, against a zero-light baseline already at 18.4-21.6 ms. 4 traversals is ' +
      '11.8 ms; the old ceiling of 6 is 17.8 ms and over frame by itself.',
  };
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ── ⭐ B4 · THE LOCAL SHADOW BUDGET AND THE SLOT SELECTOR — PURE, CAMERA AS THREE NUMBERS ─────
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * What the tier can spend on LOCAL (point/spot) shadow passes is whatever is left of
 * `lightCostBudget(tier).shadowTraversals` after the sun took its cascades: tier 2 is 4 − 3 = 1,
 * tiers 1/0 are 1 − 1 = 0, and under `?csm=0` (the single origin map) tier 2 is 4 − 1 = 3. The
 * sun's traversals are PASSED rather than looked up because the live number is `csmRig.policy()`'s
 * — the boot override, the authored `cascades`, and the two-frame swap all move it — and a budget
 * computed from the tier's default row would be right on exactly the frames nobody edited.
 *
 * The selector is nearest-to-camera, greedy by `faces`, with hysteresis for the incumbents. It is
 * pure and takes the camera as `{x, y, z}` (a three `Vector3` satisfies it structurally, no import)
 * so `tests/renderShadowBudget.test.ts` can drive 240 frames of a walking camera with no renderer
 * — the same discipline `tickShadows` keeps by taking its frame index as an argument.
 */

/** Anything with three numeric fields — `THREE.Vector3` structurally, with no `three` import. */
export interface Vec3Like {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface ShadowSlotCandidate {
  readonly id: string;
  /**
   * World position, or `null` for a light with no position — a directional light in `lights[]`
   * (the sun-copy the editor authors with Ctrl+D). A directional is everywhere it lights, so it is
   * at distance 0 from every camera and competes as the nearest thing there is; it is still one
   * traversal, so it fits wherever a spot does.
   */
  readonly pos: Vec3Like | null;
  /** Depth passes this light costs while live: 1 for a spot/directional, 6 for a point. */
  readonly faces: number;
}

export interface ShadowSlotSelection {
  /** Ids holding a live slot after this selection, in the order they were granted. */
  readonly live: readonly string[];
  /** Ids left frozen, nearest first. */
  readonly frozen: readonly string[];
  /** Traversals the live set actually spends, ≤ the budget. */
  readonly spent: number;
}

/**
 * How many metres CLOSER a challenger must be than an incumbent to take its slot. A camera
 * standing exactly between two lamps would otherwise flip the slot on every selection, and each
 * flip re-renders a frozen map (one full traversal) — the same class of hitch `everyNFrames`'
 * per-light phase exists to prevent. 2 m is a stride: a player who genuinely walked to the next
 * lamp has crossed it; one dithering at a doorway has not.
 */
export const SHADOW_SLOT_HYSTERESIS_M = 2;

/**
 * Frames between selections. Distances change by a stride every ~8 frames at a walk, the sort is
 * over ≤ 30 lights, and selecting every frame would only make the hysteresis do more work.
 */
export const SHADOW_SLOT_REFRESH_FRAMES = 8;

/** Traversals left for local lights after the sun's. Never negative: an over-budget sun is the sun row's own warning. */
export function localShadowBudget(tier: number, sunTraversals: number): number {
  return Math.max(0, lightCostBudget(tier).shadowTraversals - sunTraversals);
}

/**
 * ⭐ THE ONE SELECTION. Nearest to the camera first; an incumbent's distance is discounted by
 * `hysteresisM` so it keeps its slot until a challenger is genuinely closer; then a greedy fill
 * by `faces` that SKIPS what does not fit and keeps going — a six-face point light two metres
 * away must not block a one-face spot ten metres away from the slot it can use. Ties (and every
 * light at distance 0) keep input order, which is the world's authoring order and therefore
 * stable across reloads; `Array.prototype.sort` is stable by specification.
 *
 * Deterministic in its inputs and free of a clock, so the same camera walk selects the same
 * sequence every run — the property the 240-frame test pins.
 */
export function selectLiveShadowSlots(
  candidates: readonly ShadowSlotCandidate[],
  cameraPos: Vec3Like,
  budgetTraversals: number,
  incumbents: ReadonlySet<string>,
  hysteresisM: number = SHADOW_SLOT_HYSTERESIS_M,
): ShadowSlotSelection {
  const ranked = candidates
    .map((c, order) => {
      const d =
        c.pos === null
          ? 0
          : Math.hypot(c.pos.x - cameraPos.x, c.pos.y - cameraPos.y, c.pos.z - cameraPos.z);
      return { c, order, key: incumbents.has(c.id) ? d - hysteresisM : d };
    })
    .sort((a, b) => a.key - b.key || a.order - b.order);
  const live: string[] = [];
  const frozen: string[] = [];
  let remaining = budgetTraversals;
  for (const { c } of ranked) {
    if (c.faces > 0 && c.faces <= remaining) {
      live.push(c.id);
      remaining -= c.faces;
    } else {
      frozen.push(c.id);
    }
  }
  return { live, frozen, spent: budgetTraversals - remaining };
}

/**
 * The slot state `resolveLightRig` prices under, as DATA from the runtime rig. `null` (the pure
 * default) means no selection exists and every budgeted light is priced at its peak.
 */
export interface ShadowSlotState {
  readonly live: ReadonlySet<string>;
  readonly frozen: ReadonlySet<string>;
  /** `localShadowBudget(tier, sun)` at the last selection — what the frozen note quotes. */
  readonly budgetTraversals: number;
  readonly tier: number;
}

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ── ⭐ THE RESOLVED RIG — ONE LIST, AND EVERY ROW CARRIES ITS OWN WRITE TARGET ──────────────────
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 */

/**
 * The SKY row's verdict. A `SkyMesh` is GEOMETRY, not a light: `HemisphereLight` and
 * `SparseProbeVolume` each ADD a shading term, and the dome adds NONE. It is a surface that emits,
 * exactly like a bright wall, and it reaches materials through exactly ONE path — the probe volume
 * that photographs it. Putting the one thing in this block that is not a light into a list of
 * lights is how that distinction gets lost, and losing it is how a third implementation of the
 * indirect term gets built.
 *
 * So it gets a ROW (selectable, inspectable, priced) and NO three light object: `presence: 'none'`
 * with `supported: true`. Those two together mean "honoured, and it is not a light" — a different
 * statement from a refusal, where `supported` is false and nothing is built either.
 */
export function resolveSkyRowSupport(enabled: boolean): LightSupport {
  return {
    supported: true,
    presence: 'none',
    // The bake photographs the SCENE GRAPH, and an enabled dome is in it. That is the whole
    // mechanism by which this world has a skylight at all.
    inBake: enabled,
    note: enabled
      ? 'The sky dome is GEOMETRY that EMITS, not a light: it adds no shading term of its own and ' +
        'reaches surfaces only through the probe volume that photographs it. That is what makes ' +
        'this skylight occlusion-aware. Re-bake after any change — a sky added after a bake is ' +
        'not in the atlas, and one removed after it still is.'
      : 'This world draws no sky dome. Turning it on adds a real emitter the next bake will ' +
        'photograph, which is how a skylight is authored on this stack — there is no lights[] ' +
        'skylight, and that type is refused with this row named as its destination.',
  };
}

/**
 * One row of the unified light list. Everything a surface needs to draw, price, select and EDIT a
 * light, whichever of the two tables it came from.
 */
export interface ResolvedLight {
  /** `'sun'` | `'sky'` | the authored `cfg.id`. Row identity; `writeTarget` is the disambiguator. */
  id: string;
  role: LightRole;
  /** The sun resolves as `directional`, the sky as `skylight` — the vocabulary is already right. */
  type: LightType;
  mobility: LightMobility;
  support: LightSupport;
  /** `support.presence`, lifted so a consumer does not have to reach through two objects. */
  presence: LightPresence;
  /**
   * ⭐ WHERE AN INSPECTOR WRITES. The whole point of carrying it: the inspector DISPATCHES on this
   * string, it never chooses a destination from `role`. One list, two (three) write paths, and the
   * branch lives in the data rather than in every consumer.
   */
  writeTarget: string;
  /** Index into `lighting.lights[]`, or null for the sun and the sky. */
  index: number | null;
  /**
   * A fixed row cannot be deleted or re-typed: `lighting.sun` and `lighting.sky` are ONE each by
   * construction. A world that genuinely needs two suns authors a second `directional` in
   * `lights[]` — which already works. `lighting.sun` keeps one meaning forever: the directional
   * light the sky dome is aimed from and the one the atlas header fingerprints.
   */
  fixed: boolean;
  /** Fields the inspector shows, hides, and relabels. `lightFields(role, type)`. */
  fields: LightFieldPlan;
  /** `fields.hidden` — §1's (type × field) matrix, as DATA so one test can assert the whole thing. */
  meaninglessFields: readonly string[];
  /** `lightCostUnits` for this row. 0 for anything not present at runtime. */
  costUnits: number;
  /**
   * `shadowTraversals` for this row, AMORTISED over the update policy: a spot/directional is 1, a
   * point is 6, an `everyNFrames` light is that over N, and a `staticOnly` or static/refused one
   * is 0. The number that predicts the milliseconds; see the function for why it is the mean.
   */
  shadowTraversals: number;
  /** L2. The shadow update policy this row resolved to — `'everyFrame'` for the sun and the sky. */
  shadowUpdate: ShadowUpdate;
  /**
   * B4. What the light's map ACTUALLY does under `shadowUpdate` — `effectiveShadowUpdate`. Equal
   * to `shadowUpdate` on the three honoured arms; on `'budgeted'` it is `everyFrame` while the
   * light holds a slot and `staticOnly` while it does not. `shadowTraversals` and `costUnits`
   * above are priced from THIS, so a frozen row reads 0 and a live one its faces.
   */
  effectiveShadowUpdate: AppliedShadowUpdate;
  /**
   * B4. `'live'` / `'frozen'` for a runtime `budgeted` light once the rig has selected, `'n/a'`
   * for every other arm and for a budgeted light priced with no selection in hand. Published per
   * row through `__fwg.state().lights.rows[i].slot` — the state field that proves the split is
   * reached, read off the rig's own bookkeeping and not off a screenshot.
   */
  slot: ShadowSlot;
  /**
   * L2. VRAM this row's shadow map costs, `mapSize² × 8 B × (point ? 6 : 1)`. The number
   * `censusScene` declared it could not see: on Sponza the SUN alone is 2048² × 8 = 33.55 MB.
   */
  shadowMapBytes: number;
  /**
   * Conditions that are MEANINGFUL BUT COSTLY OR SURPRISING — never a refusal, never a hidden
   * field, and each one appears only while its condition holds. Data, so the editor renders them
   * and a test can assert they exist and are reachable.
   */
  notes: readonly string[];
}

/**
 * The structural shape `resolveLightRig` reads. Declared here rather than imported from
 * `world/schema.ts` because that module imports THIS one — see `WORLD_LIGHT_FIELDS`' note. The
 * fence is a compile-time assignability assertion in `tests/renderLightRig.test.ts`: a real
 * `parseWorld(...).lighting` is assigned to this type, so a schema field that changes shape fails
 * the typecheck rather than silently reading `undefined`.
 */
export interface LightRigInput {
  readonly sun: {
    readonly azimuthDeg: number;
    readonly elevationDeg: number;
    readonly castShadow: boolean;
    /**
     * L2. Read for one thing: the sun's shadow map is the single largest VRAM item in nearly every
     * world (2048² on both `sandbox-01` and `sponza-debug-01` — 33.55 MB each), and a shadow-VRAM
     * ledger that omitted it would be a ledger of the minority.
     */
    readonly shadowMapSize: number;
    /** B3 — `sunShadowPolicy` reads these two; absent or 0 means the tier's row. */
    readonly cascades?: number | undefined;
    readonly shadowDistanceM?: number | undefined;
    /**
     * ⭐ B6 — THE SUN'S SHADOW SCHEDULE, WHICH L2 SAID IT COULD NEVER HAVE. The obstacle it named
     * was real and is unchanged (`WorldSunSchema` is `prefault({})`, so a `.default()`ed field
     * materialises into every parse and moves all six committed `worldHash`es); what it missed is
     * that `.optional()` with NO default does not — `canonicalJson` drops `undefined`, which is
     * exactly how B3 landed `cascades`/`shadowDistanceM` beside it. Absent ⇒ `'everyFrame'`.
     */
    readonly shadowUpdate?: AppliedShadowUpdate | undefined;
    readonly shadowUpdateN?: number | undefined;
  };
  readonly sky: { readonly enabled: boolean };
  readonly lights?: readonly LightRowInput[] | undefined;
  /** `'fill' | 'baked'` — read ONLY to say whether a `static` light is currently in anything. */
  readonly indirect: string;
}

/** The fields of one authored light this fold reads. `WorldLightConfig` satisfies it. */
export interface LightRowInput {
  readonly id: string;
  readonly type: LightType;
  readonly mobility: LightMobility;
  readonly castShadow: boolean;
  readonly elevationDeg: number;
  /** L2 — priced into `shadowMapBytes`. */
  readonly shadowMapSize: number;
  /** L2 — scales `shadowTraversals` and `costUnits`. */
  readonly shadowUpdate: ShadowUpdate;
  readonly shadowUpdateN: number;
}

/**
 * ⭐ THE FOLD. `world.lighting` → one list of rows: the sun, the sky, then every authored light in
 * array order. PURE and total — every input produces a row, including a refused one, because a
 * refused light that vanishes from the list is exactly the silent downgrade the refusal table
 * exists to prevent.
 *
 * The sun resolves as `stationary` and that is a FACT about today's code, not a policy: `scene.ts`
 * adds it once and never removes it for a bake, so it is in the scene AND in the atlas. (Its
 * DIRECT beam is still excluded from the atlas, because `src/render/sky.ts` trap 4 hides the solar
 * disc for every capture — the same reason a punctual light's bounce is not a double count.)
 *
 * `sunShadow` (B3) is the sun's cascade policy, and its DEFAULT IS THE SINGLE MAP — omitted, the
 * sun row prices exactly what it priced in L1/L2 (1 traversal, `mapSize² × 8 B`), so every pin
 * taken on that arm still holds. `worldLights.describeLightRig` is the tier-aware caller and passes
 * the LIVE policy from `csmRig`, which is where the editor's line moves to `3/4` at tier 2 —
 * deliberately, and in one place.
 */
export function resolveLightRig(
  lighting: LightRigInput,
  sunShadow: SunShadowPolicy | null = null,
  /**
   * B4. The rig's current slot assignment for `budgeted` lights. `null` — the pure default —
   * prices every budgeted light at its peak (`slot: 'n/a'`), the same conservative rule
   * `shadowTraversals` keeps for an unmigrated caller; `worldLights.describeLightRig` passes the
   * live one, which is where a frozen row reads 0.
   */
  slots: ShadowSlotState | null = null,
  /**
   * ⭐ PREVIEW UNBAKED STATIC LIGHTS. `false` — the pure default — is the behaviour every existing
   * caller and every committed pin was taken on, which is why it is a fourth optional argument and
   * not a field on `LightRigInput`: nothing on disk moves, so none of the six `worldHash` pins can.
   *
   * ⚠️ AND THE FOLD MUST LEARN IT OR THE BUDGET LINE LIES. A `bake`-presence light is priced at 0
   * here (`lightCostUnits` returns 0 for any non-`runtime` presence). If the RIG parents a light
   * for preview and this fold does not know, the editor's frame-cost row says "free, baked" over a
   * frame that is paying ~0.24 ms for it unshadowed, or ~2.96 ms per depth pass if it casts.
   */
  previewStatic = false,
  /**
   * ⭐ THE LOCAL SHADOW-MAP CLAMP, SO THE PRICED MB AND THE ALLOCATED MB CANNOT DRIFT. `null` —
   * the pure default — prices `cfg.shadowMapSize` raw, which is what every committed pin was taken
   * on. `worldLights.describeLightRig` (the tier-aware caller) passes the SAME cap and scale
   * `worldLights.applyShadow` writes with, which is the `effectiveShadowUpdate` precedent: one
   * resolver, and the row's promise cannot say 50.3 MB over a graph holding 12.6.
   */
  localShadow: { readonly cap: number; readonly scale: number } | null = null,
): ResolvedLight[] {
  const rows: ResolvedLight[] = [];
  const cascaded = sunShadow !== null && sunShadow.enabled;
  /*
   * ⭐ B6: THE SUN'S ARM, THROUGH THE SAME ONE RESOLVER EVERY OTHER ROW USES. `sunShadow` is null
   * for a pure caller with no tier in hand, so the arm is read off the WORLD BLOCK rather than off
   * the policy — the policy is a fold OVER this same field and the two cannot disagree, but only
   * one of them exists in every caller.
   */
  const sunUpdate = sunShadowUpdate(lighting.sun);
  const sunUpdateN = normalizeShadowUpdateN(lighting.sun.shadowUpdateN);

  const sunSupport = resolveLightSupport('directional', 'stationary');
  rows.push({
    id: 'sun',
    role: 'sun',
    type: 'directional',
    mobility: 'stationary',
    support: sunSupport,
    presence: sunSupport.presence,
    writeTarget: 'lighting.sun',
    index: null,
    fixed: true,
    fields: lightFields('sun', 'directional'),
    meaninglessFields: lightFields('sun', 'directional').hidden,
    // B3: a cascade is a depth pass and a depth pass is one cost unit (`lightCostUnits` derives the
    // shadow term from `shadowTraversals` in the same units), so the cascaded sun is 1 + N.
    costUnits: cascaded
      ? 1 + sunShadow.traversals
      : lightCostUnits({
          type: 'directional',
          castShadow: lighting.sun.castShadow,
          presence: 'runtime',
          shadowUpdate: sunUpdate,
          shadowUpdateN: sunUpdateN,
        }),
    /*
     * B3: under cascades the sun's OWN map is never built (`setupShadowNode` is not called when
     * `shadow.shadowNode` is set) — N cascades are N traversals and N maps, priced by the policy.
     * B6: and `SunShadowPolicy.traversals` is ALREADY amortised by the arm, through the same
     * `amortiseShadowTraversals` this line's other branch reaches via `shadowTraversals`. One rule.
     */
    shadowTraversals: cascaded
      ? sunShadow.traversals
      : shadowTraversals('directional', lighting.sun.castShadow, 'runtime', sunUpdate, sunUpdateN),
    // B6: authored, `.optional()`, absent ⇒ `everyFrame`. It is stated as DATA on the row (rather
    // than left for a consumer to infer from the field's absence) for the reason L2 stated it as a
    // literal when there was no field at all — a reader acts on the row, not on the schema.
    shadowUpdate: sunUpdate,
    effectiveShadowUpdate: sunUpdate,
    // The sun is never a slot candidate: its budget is the cascade policy's, and the local budget
    // is what is LEFT after it (`localShadowBudget`).
    slot: 'n/a',
    shadowMapBytes: cascaded
      ? sunShadow.bytes
      : shadowMapBytes({
          type: 'directional',
          castShadow: lighting.sun.castShadow,
          presence: 'runtime',
          shadowMapSize: lighting.sun.shadowMapSize,
        }),
    notes: lighting.sun.castShadow ? sunShadowNotes(sunUpdate, cascaded ? sunShadow : null) : [],
  });

  const skySupport = resolveSkyRowSupport(lighting.sky.enabled);
  rows.push({
    id: 'sky',
    role: 'sky',
    type: 'skylight',
    // `static` is the only honest mobility for the dome: it is photographed by the bake and it has
    // no runtime shading term of its own. It is NOT the refused `skylight × static` of the table —
    // that refusal is about a `lights[]` ENTRY, and its note names this row as the destination.
    mobility: 'static',
    support: skySupport,
    presence: skySupport.presence,
    writeTarget: 'lighting.sky',
    index: null,
    fixed: true,
    fields: lightFields('sky', 'skylight'),
    meaninglessFields: lightFields('sky', 'skylight').hidden,
    // A dome adds no shading term, so it costs no light slot and no depth pass. Its cost is a
    // draw call and the bake time it adds, neither of which this budget is in the units of.
    costUnits: 0,
    shadowTraversals: 0,
    // A dome casts no shadow and allocates no map. `'everyFrame'` is the vocabulary's zero, not a
    // claim: `shadowMapBytes` and `shadowTraversals` are both 0, which is what a reader acts on.
    shadowUpdate: 'everyFrame',
    effectiveShadowUpdate: 'everyFrame',
    slot: 'n/a',
    shadowMapBytes: 0,
    notes:
      lighting.sky.enabled && lighting.indirect === 'fill' ? [SKY_DRAWN_NOT_LIT_NOTE] : [],
  });

  const authored = lighting.lights ?? [];
  for (let i = 0; i < authored.length; i++) {
    const cfg = authored[i]!;
    // ⭐ ONE resolve, then ONE adjustment — the same function `worldLights.reconcile` calls, so the
    // row this prices and the light the frame draws can never disagree about presence.
    const support = previewPresence(resolveLightSupport(cfg.type, cfg.mobility), previewStatic);
    const plan = lightFields('authored', cfg.type);
    /*
     * B4: a slot is a fact about a RUNTIME `budgeted` caster and nothing else. A bake-only light
     * has no per-frame pass to budget; an unshadowed one holds no map; a movable one cannot freeze
     * (`effectiveShadowUpdate`). Everything else reads the rig's assignment, and a budgeted light
     * the rig has not yet placed (`slots === null`, or a fresh id) is `'n/a'` — priced LIVE.
     */
    const budgetable =
      cfg.shadowUpdate === 'budgeted' &&
      cfg.castShadow &&
      support.presence === 'runtime' &&
      cfg.mobility !== 'movable';
    const slot: ShadowSlot =
      budgetable && slots !== null
        ? slots.live.has(cfg.id)
          ? 'live'
          : slots.frozen.has(cfg.id)
            ? 'frozen'
            : 'n/a'
        : 'n/a';
    const effective = effectiveShadowUpdate(cfg.shadowUpdate, cfg.mobility, slot);
    rows.push({
      id: cfg.id,
      role: 'authored',
      type: cfg.type,
      mobility: cfg.mobility,
      support,
      presence: support.presence,
      writeTarget: `lighting.lights[${i}]`,
      index: i,
      fixed: false,
      fields: plan,
      meaninglessFields: plan.hidden,
      // Priced from the EFFECTIVE arm, so a frozen budgeted row costs what its frozen map costs
      // (0 traversals) and the frame-cost line adds up to what the frame draws.
      costUnits: lightCostUnits({
        type: cfg.type,
        castShadow: cfg.castShadow,
        presence: support.presence,
        shadowUpdate: effective,
        shadowUpdateN: cfg.shadowUpdateN,
      }),
      shadowTraversals: shadowTraversals(
        cfg.type,
        cfg.castShadow,
        support.presence,
        effective,
        cfg.shadowUpdateN,
      ),
      shadowUpdate: cfg.shadowUpdate,
      effectiveShadowUpdate: effective,
      slot,
      shadowMapBytes: shadowMapBytes({
        type: cfg.type,
        castShadow: cfg.castShadow,
        presence: support.presence,
        // `cfg.shadowMapSize` is the ARTIST'S REQUEST since the shadowRes stage; what is allocated
        // is `shadowMapSizeFor(request, tier cap, session scale)` and this row must price THAT.
        shadowMapSize:
          localShadow === null
            ? cfg.shadowMapSize
            : shadowMapSizeFor(cfg.shadowMapSize, localShadow.cap, localShadow.scale),
      }),
      notes: authoredLightNotes(cfg, support, lighting.indirect, slot, slots),
    });
  }
  return rows;
}

/**
 * ── THE THIRD TIER OF WARNING: MEANINGFUL BUT COSTLY OR SURPRISING ─────────────────────────────
 *
 * Tier 1 is a REFUSAL (`support.supported === false`) — the pair cannot be honoured at all.
 * Tier 2 is a HIDDEN FIELD (`meaninglessFields`) — it asks no question.
 * Tier 3 is this: the setting IS honoured, and the artist would be surprised by what it costs or
 * by what it does. One short sentence per cause, present only while the cause is.
 */
export const SHADOW_COST_NOTE =
  'Each live shadow caster is a depth pass over the scene - ~2.96 ms measured on Sponza, and a ' +
  'POINT light is six of them (one per cube face). Shadow cost scales with CONTENT, not with ' +
  'resolution, so dynamic resolution cannot recover any of it.';

export const STATIC_UNBAKED_NOTE =
  'THIS LIGHT IS INVISIBLE RIGHT NOW. It is static, so it lives only in the probe volume - and ' +
  "this world's indirect term is the constant fill, which no light is in. Bake this world (and " +
  "set Indirect light to 'baked'), or set mobility to movable.";

export const SPOT_AIMS_DOWN_NOTE =
  'Angles say where the light comes FROM, so a spot at a POSITIVE elevation shines DOWNWARD. One ' +
  'aiming convention for the whole lighting block - the sun’s - or two rigs disagree.';

export const SKY_DRAWN_NOT_LIT_NOTE =
  'The sky is DRAWN but not lit from: this world’s indirect term is the constant fill, which ' +
  'knows nothing about the dome. Bake the world to make this sky an actual skylight.';

export const STATIC_SHADOW_UPDATE_INERT_NOTE =
  'Shadow update does nothing on a STATIC light: it is not in the scene at runtime, so there is no ' +
  'per-frame depth pass to skip. The bake renders its shadow map once, whatever this says.';

export const STATIC_SHADOW_IS_A_BAKE_SETTING_NOTE =
  'castShadow on a STATIC light costs nothing per frame (the light is not in the scene) and is ' +
  'not ignored either: the bake renders its shadow map ONCE and every capture is occluded by it. ' +
  'Turning it off makes this light bleed through walls in the atlas.';

export const SHADOW_STATIC_ONLY_NOTE =
  'This light’s shadow is rendered ONCE from static geometry and then frozen, so it costs nothing ' +
  'per frame - and NOTHING THAT MOVES CASTS A SHADOW FROM IT, the player included. three gives one ' +
  'shadow map per light, so there is nowhere to put the per-object dynamic shadow UE would add on ' +
  'top. The map is re-rendered when the light or the world is edited.';

/**
 * ⭐ B6 — THE SUN'S FROZEN ARM SAYS SOMETHING A LOCAL LIGHT'S DOES NOT, so it is its own sentence.
 *
 * `SHADOW_STATIC_ONLY_NOTE` says "nothing that moves casts a shadow from this light". On a lamp
 * that is one lamp; on the SUN it is every shadow in the level — the player, the companion, every
 * moving prop, everywhere, all at once. And the second half is unique to the sun: the arm gives up
 * the CASCADES (`sunShadowPolicy` — a camera-fitted box cannot be frozen honestly), so the reach
 * changes from "follows the camera to Shadow distance" to "a fixed box about the world origin".
 * Both are OWNER trades, which is why they are a note on the row and not a default anywhere.
 */
export const SUN_SHADOW_STATIC_ONLY_NOTE =
  'The SUN’s shadow is rendered ONCE from static geometry and frozen: 0 ms per frame, and NOTHING ' +
  'THAT MOVES CASTS A SUN SHADOW - the player, the companion and every moving prop, everywhere. ' +
  'It also drops the cascades: a cascade box is fitted to the camera, so the frozen map is a ' +
  'world-anchored +/- Shadow extent (m) box about the ORIGIN, and geometry outside it is lit.';

export const SHADOW_EVERY_N_NOTE =
  'This light’s shadow re-renders every Nth frame, so its AVERAGE cost is that fraction of a full ' +
  'depth pass - but the refresh frame still costs the whole pass, and a moving caster’s shadow ' +
  'lags it by up to N frames.';

/**
 * B4 — the budgeted arm's three sentences. LIVE says the light is paying full price and why it
 * may stop; FROZEN names the count, the budget and the tier (the (N+1)th light's own explanation of
 * what it is not doing); MOVABLE says the arm is inert on a light that cannot freeze. Each is
 * present only while its cause is, like every note here.
 */
export const SHADOW_BUDGETED_LIVE_NOTE =
  'LIVE: this light is the nearest budgeted caster, so its shadow is a full depth pass every ' +
  'frame with moving things in it. It freezes the moment another budgeted light is nearer.';

export const SHADOW_BUDGETED_MOVABLE_NOTE =
  'Budgeted does nothing on a MOVABLE light: a frozen map would be a picture from wherever the ' +
  'light stood when it froze. It renders every frame, at full price. Make it stationary to budget it.';

/** The frozen row's sentence — with the numbers, so the artist can see what would change it. */
export function shadowBudgetedFrozenNote(
  budgetedCasters: number,
  budgetTraversals: number,
  tier: number,
  faces: number,
): string {
  const why =
    faces > budgetTraversals
      ? `this light needs ${faces} traversal(s) per frame and the tier affords ${budgetTraversals}, so it can never be live`
      : `${budgetedCasters} budgeted caster(s) for ${budgetTraversals} live traversal(s) at tier ${tier}, nearest wins`;
  return (
    `Frozen: ${why}; moving things cast no shadow from this light until it is nearest. Static ` +
    'geometry still shadows it correctly from its frozen map.'
  );
}

/**
 * B6 — the sun row's notes, one arm each, present only while the cause is. Same tiering as
 * `authoredLightNotes` below: a note is MEANINGFUL, HONOURED and SURPRISING, never a refusal.
 * `sunShadow` is the policy when it is cascaded, `null` otherwise (single map or `?csm=0`).
 */
function sunShadowNotes(
  update: AppliedShadowUpdate,
  sunShadow: SunShadowPolicy | null,
): readonly string[] {
  if (update === 'staticOnly') return [SUN_SHADOW_STATIC_ONLY_NOTE];
  const notes: string[] =
    update === 'everyNFrames' ? [SHADOW_EVERY_N_NOTE] : [SHADOW_COST_NOTE];
  if (sunShadow !== null) notes.push(sunCascadesNote(sunShadow));
  return notes;
}

function authoredLightNotes(
  cfg: LightRowInput,
  support: LightSupport,
  indirect: string,
  slot: ShadowSlot,
  slots: ShadowSlotState | null,
): readonly string[] {
  const notes: string[] = [];
  if (!support.supported) return notes; // a refusal is the whole message; nothing else applies
  /*
   * ⭐ PREVIEW IS DERIVABLE FROM WHAT THIS FUNCTION ALREADY HOLDS, so it takes no extra argument
   * and cannot be passed a value that disagrees with the presence it is describing:
   * `resolveLightSupport(type, 'static')` is `presence: 'bake'` for EVERY punctual type, so a
   * `static` light reading `runtime` here is a previewing one and nothing else.
   *
   * The note goes FIRST because it is the reason the rest of the list changed shape — this light
   * has just started reporting shadow costs it did not report a moment ago.
   */
  const previewing = support.presence === 'runtime' && cfg.mobility === 'static';
  if (previewing) notes.push(STATIC_PREVIEW_NOTE);
  if (support.presence === 'bake') {
    if (indirect !== 'baked') notes.push(STATIC_UNBAKED_NOTE);
    if (cfg.castShadow) notes.push(STATIC_SHADOW_IS_A_BAKE_SETTING_NOTE);
    /*
     * ⚠️ A `static` light's shadow policy is INERT and saying so is the point. It has no runtime
     * presence, so there is no per-frame pass to skip; `freezeSceneForBake` already renders its map
     * exactly once per bake. Silently accepting `staticOnly` here would let an artist believe they
     * had bought a saving on a light that was already free, which is the same class of lie as the
     * `static + castShadow` normalisation L1 refused to ship.
     */
    if (cfg.shadowUpdate !== 'everyFrame') notes.push(STATIC_SHADOW_UPDATE_INERT_NOTE);
  } else if (cfg.castShadow) {
    if (cfg.shadowUpdate === 'staticOnly') notes.push(SHADOW_STATIC_ONLY_NOTE);
    else if (cfg.shadowUpdate === 'everyNFrames') notes.push(SHADOW_EVERY_N_NOTE);
    else if (cfg.shadowUpdate === 'budgeted') {
      if (cfg.mobility === 'movable') notes.push(SHADOW_BUDGETED_MOVABLE_NOTE, SHADOW_COST_NOTE);
      else if (slot === 'frozen' && slots !== null) {
        notes.push(
          shadowBudgetedFrozenNote(
            slots.live.size + slots.frozen.size,
            slots.budgetTraversals,
            slots.tier,
            cfg.type === 'point' ? POINT_SHADOW_FACES : 1,
          ),
        );
      } else notes.push(SHADOW_BUDGETED_LIVE_NOTE, SHADOW_COST_NOTE);
    } else notes.push(SHADOW_COST_NOTE);
  }
  if (cfg.type === 'spot' && cfg.elevationDeg > 0) notes.push(SPOT_AIMS_DOWN_NOTE);
  return notes;
}

/**
 * ⭐ B4 — THE FRAME-COST LINE'S LOCAL-SHADOW SENTENCE, AND THE OVER-BUDGET LEVER, AS DATA.
 *
 * Two sentences the editor appends under the traversal count, both pure so the exact words are
 * pinned headlessly. The first is the split's state (`local: 1 live / 1 frozen`), present only
 * while a budgeted caster exists. The second is what to do when the line reads over budget —
 * REPORTED, never enforced (the standing rule): it names the cheapest lever that is actually
 * available on this rig, in order — freeze a cascade, mark a live caster budgeted, drop a count.
 */
export function describeLocalShadowBudget(
  rows: readonly ResolvedLight[],
  budgetTraversals: number,
): string | null {
  const live = rows.filter((r) => r.slot === 'live').length;
  const frozen = rows.filter((r) => r.slot === 'frozen').length;
  if (live + frozen === 0) return null;
  return `local: ${live} live / ${frozen} frozen (${budgetTraversals} traversal(s) after the sun)`;
}

export function describeShadowOverBudget(
  rows: readonly ResolvedLight[],
  cost: { shadowTraversals: number },
  budget: LightCostBudget,
  sunShadow: SunShadowPolicy,
): string | null {
  const over = Math.round((cost.shadowTraversals - budget.shadowTraversals) * 100) / 100;
  if (over <= 0) return null;
  const unbudgeted = rows.filter(
    (r) =>
      r.role === 'authored' &&
      r.presence === 'runtime' &&
      r.shadowTraversals > 0 &&
      r.shadowUpdate === 'everyFrame' &&
      r.mobility !== 'movable',
  );
  const lever =
    unbudgeted.length > 0
      ? `set ${unbudgeted.map((r) => `'${r.id}'`).join(', ')} to Shadow update 'budgeted' so only the nearest stays live`
      : sunShadow.enabled && sunShadow.cascades > 1
        ? `freeze the far cascade (${sunShadow.mapSizes[sunShadow.cascades - 1]} px, ${sunShadow.breaksM[sunShadow.cascades - 2]}–${sunShadow.distanceM} m) or drop the sun to ${sunShadow.cascades - 1} cascade(s)`
        : 'turn castShadow off on the light that needs it least';
  return `over by ${over} traversal(s) — ${lever}`;
}

/**
 * The whole rig's per-frame price, summed over rows. Two numbers, never one, because they scale
 * with different things (see the cost-model header) and a single figure would hide which lever
 * moves them.
 */
export function rigCost(rows: readonly ResolvedLight[]): {
  costUnits: number;
  shadowTraversals: number;
  /**
   * L2. Shadow VRAM over the WHOLE rig, sun included. A third unit that mixes with neither of the
   * other two on purpose: cost units are per-fragment ms, traversals are per-scene ms, and this is
   * bytes that are resident whether or not a frame is drawn.
   */
  shadowMapBytes: number;
} {
  let costUnits = 0;
  let traversals = 0;
  let bytes = 0;
  for (const r of rows) {
    costUnits += r.costUnits;
    traversals += r.shadowTraversals;
    bytes += r.shadowMapBytes;
  }
  // Float addition of 0.08 arms is not exact; one decimal is the resolution this number is
  // meaningful at (0.1 cost unit ≈ 0.3 ms) and it keeps the readout from printing 3.0000000004.
  // `shadowTraversals` gets the same treatment for the same reason — an `everyNFrames` point light
  // contributes 6/4 = 1.5 and three of them would otherwise print 4.499999999999999.
  return {
    costUnits: Math.round(costUnits * 10) / 10,
    shadowTraversals: Math.round(traversals * 100) / 100,
    shadowMapBytes: bytes,
  };
}
